// This DataTable is failing for some reason
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from './services/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PdfUpload from "./PdfUpload";
import ExcelUpload from "./ExcelUpload";
import './App.css';
import GroupedBarChart from "./GroupedCreditCardBarChart";
import TransactionsPieChart from "./TransactionsPieChart";
import AssetExpenseLinkModal from './components/AssetExpenseLinkModal.jsx';
import IncomeSplitModal from './components/IncomeSplitModal';
import IncomeLinkModal from './components/IncomeLinkModal.jsx';
import DebtDisbursementLinkModal from './components/DebtDisbursementLinkModal.jsx';
import Modal from './components/Modal';
import { useReceipts } from './hooks/useReceipts';
import ReceiptModal from './components/ReceiptModal';
import StatementSummaryForm from './NewRevolverStatementSummary';
import resolveCardDisplay from './utils/resolveCardDisplay';
import { buildCategoryTreeSorted } from './utils/expenseCategories';
import { exportToExcel } from './utils/exportUtils';

import { buildAssetLabel, getDomainForCategory } from './utils/assetLabelHelper';

const MemoizedEditableCell = React.memo(({ transaction, field, displayValue, isEditing, onStartEdit, onCancel, onSave, saving, className = '' }) => {
    const [tempValue, setTempValue] = useState(displayValue);

    useEffect(() => {
        if (isEditing) {
            setTempValue(displayValue);
        }
    }, [isEditing, displayValue]);

    if (isEditing) {
        return (
            <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <input
                    type={field === 'transaction_date' || field === 'post_date' ? 'date' : field === 'amount' ? 'number' : 'text'}
                    className="form-control form-control-sm"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onSave(tempValue);
                        else if (e.key === 'Escape') onCancel();
                    }}
                    autoFocus
                    step={field === 'amount' ? '0.01' : undefined}
                />
                <button className="btn btn-sm btn-success" onClick={() => onSave(tempValue)} disabled={saving}>
                    {saving ? '...' : '✓'}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={onCancel} disabled={saving}>✗</button>
            </div>
        );
    }

    return (
        <div
            className={`editable-cell ${className}`}
            style={{ position: 'relative', cursor: 'pointer', padding: '4px', transition: 'background-color 0.2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1f2937'; e.currentTarget.style.borderRadius = '4px'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onStartEdit(transaction.id, field, transaction[field]); }}
        >
            <span>{displayValue}</span>
            <span className="edit-icon" style={{ opacity: 0, marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>✏️</span>
        </div>
    );
});

const TransactionRow = React.memo(({
    transaction,
    id,
    deleting,
    expandedTxns,
    editingCell,
    saving,
    formatDate,
    formatCurrency,
    startEditing,
    cancelEditing,
    saveEdit,
    setExpandedTxns,
    renderExpenseCategory,
    buildAssetLabel,
    getDomainForCategory,
    setUtilityRow,
    setShowUtilityModal,
    setShowSplitModal,
    setSplitRow,
    deleteTransaction,
    openReceipt,
    setShowIncomeSplitModal,
    setIncomeSplitRow,
    setIncomeSplitDefaultKind,
    setShowIncomeLinkModal,
    setIncomeLinkRow,
    setShowDebtDisbursementModal,
    setDebtDisbursementRow,
    setActivePaymentTxnId,
    setShowPaymentModal,
    duplicateTransaction
}) => {
    return (
        <tr key={transaction.id}
            className={deleting.has(transaction.id) ? 'table-secondary' : ''}
            style={{
                backgroundColor: transaction._isChild ? '#1f2a44' : transaction.is_pending ? '#2a2a1a' : 'transparent',
                borderLeft: transaction._isChild ? '3px solid #60a5fa' : transaction.is_pending ? '3px solid #f59e0b' : 'none',
                opacity: transaction.is_pending ? 0.85 : 1,
            }}
        >
            <td>
                <MemoizedEditableCell
                    transaction={transaction}
                    field="transaction_date"
                    displayValue={transaction.transaction_date ? formatDate(transaction.transaction_date) : ''}
                    isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'transaction_date'}
                    onStartEdit={startEditing}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                    saving={saving}
                />
            </td>
            <td>
                <MemoizedEditableCell
                    transaction={transaction}
                    field="post_date"
                    displayValue={transaction.post_date ? formatDate(transaction.post_date) : ''}
                    isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'post_date'}
                    onStartEdit={startEditing}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                    saving={saving}
                />
            </td>
            {/* Card column - only show for "All Cards" view */}
            {id === 'all' && (
                <td>
                    {transaction.card_display || 'N/A'}
                </td>
            )}
            <td>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    {/* Expand/collapse toggle for parents with children */}
                    {(!transaction._isChild && transaction.has_children) ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpandedTxns(prev => {
                                    const n = new Set([...prev]);
                                    if (n.has(transaction.id)) n.delete(transaction.id); else n.add(transaction.id);
                                    return n;
                                });
                            }}
                            title={expandedTxns.has(transaction.id) ? 'Collapse' : 'Expand'}
                            style={{ padding:'2px 6px', fontSize: '10px', minWidth: '20px' }}
                        >
                            {expandedTxns.has(transaction.id) ? '−' : '+'}
                        </button>
                    ) : (
                        !transaction._isChild && <span style={{ width: 20 }}></span>
                    )}
                    <div style={transaction._isChild ? { paddingLeft: 24 } : {}}>
                        {transaction.is_pending && (
                            <span style={{ fontSize: '10px', fontWeight: 600, color: '#f59e0b', background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b', borderRadius: 4, padding: '1px 5px', marginRight: 6, verticalAlign: 'middle' }}>PENDING</span>
                        )}
                        <MemoizedEditableCell
                            transaction={transaction}
                            field="description"
                            displayValue={transaction.description || ''}
                            isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'description'}
                            onStartEdit={startEditing}
                            onCancel={cancelEditing}
                            onSave={saveEdit}
                            saving={saving}
                        />
                    </div>
                </div>
            </td>
            <td>
                <MemoizedEditableCell
                    transaction={transaction}
                    field="user_description"
                    displayValue={transaction.user_description || ''}
                    isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'user_description'}
                    onStartEdit={startEditing}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                    saving={saving}
                />
            </td>
            <td>
                <MemoizedEditableCell
                    transaction={transaction}
                    field="amount"
                    displayValue={formatCurrency(transaction.amount || 0)}
                    isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'amount'}
                    onStartEdit={startEditing}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                    saving={saving}
                />
            </td>
            <td style={{ minWidth:'220px', whiteSpace:'nowrap' }}>
                {renderExpenseCategory(transaction)}
            </td>
            <td>
                <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                    {(() => {
                        const aid = transaction.linked_asset_id;
                        if (!aid) {
                            const cat = String(transaction.expense_category || '');
                            const domain = getDomainForCategory(cat);
                            const isYardWaste = cat.includes('yard_waste');
                            const isWaste = (cat.startsWith('waste_') || cat.includes('sanitation')) && !isYardWaste;
                            const isLinkable = domain !== 'generic' || isWaste || isYardWaste;
                            if (!isLinkable) return null;

                            let labelBtn = 'Link Asset';
                            if (domain === 'utilities') labelBtn = 'Link Utilities';
                            else if (domain === 'home') labelBtn = 'Link Home';
                            else if (domain === 'landscaping' || isYardWaste) labelBtn = 'Link Landscaping';
                            else if (domain === 'vehicle') labelBtn = 'Link Vehicle';
                            else if (isWaste) labelBtn = 'Link Waste';

                            return (
                                <button
                                    className="btn btn-link btn-sm"
                                    style={{ color:'#93c5fd', padding:0 }}
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        setUtilityRow({
                                            id: transaction.id,
                                            sourceType: 'revolver',
                                            date: transaction.transaction_date,
                                            description: transaction.description,
                                            amount: transaction.amount,
                                            linked_asset_id: transaction.linked_asset_id || '',
                                            linked_asset_expense_id: transaction.linked_asset_expense_id || '',
                                            linked_utility_bill_id: transaction.linked_utility_bill_id || '',
                                            linked_recurring_series_id: transaction.linked_recurring_series_id || '',
                                            linked_property_id: transaction.linked_property_id || '',
                                            expense_category: transaction.expense_category || '',
                                        });
                                        setShowUtilityModal(true);
                                    }}
                                >
                                    {labelBtn}
                                </button>
                            );
                        }
                        const label = buildAssetLabel(transaction);
                        return (
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                <span style={{ color:'#60a5fa', fontSize:'0.9rem' }}>{label}</span>
                                <button
                                    className="btn btn-link btn-sm"
                                    style={{ color:'#93c5fd', padding:0 }}
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        setUtilityRow({
                                            id: transaction.id,
                                            sourceType: 'revolver',
                                            date: transaction.transaction_date,
                                            description: transaction.description,
                                            amount: transaction.amount,
                                            linked_asset_id: transaction.linked_asset_id || '',
                                            linked_asset_expense_id: transaction.linked_asset_expense_id || '',
                                            linked_utility_bill_id: transaction.linked_utility_bill_id || '',
                                            linked_recurring_series_id: transaction.linked_recurring_series_id || '',
                                            linked_property_id: transaction.linked_property_id || '',
                                            expense_category: transaction.expense_category || '',
                                            linked_todo_id: transaction.linked_todo_id || '',
                                        });
                                        setShowUtilityModal(true);
                                    }}
                                >
                                    Edit Link
                                </button>
                            </div>
                        );
                    })()}
                </span>
            </td>
            <td>
                <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                    {(() => {
                        const todoInfo = transaction.linked_todo_info;
                        if (!todoInfo) return null;

                        let url = null;
                        if (todoInfo.asset_type === 'vehicle') {
                            url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                        } else if (todoInfo.property_id) {
                            url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                        }

                        const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                        
                        const content = (
                            <span 
                                style={{ color: '#93c5fd', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.85rem' }}
                                title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}
                            >
                                {todoInfo.title}
                            </span>
                        );

                        return (
                            <>
                                {url ? (
                                    <Link to={url}>{content}</Link>
                                ) : (
                                    content
                                )}
                                <span style={{ fontSize: '10px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 4px', marginLeft: '4px' }}>
                                    {todoInfo.status_display}
                                </span>
                            </>
                        );
                    })()}
                </span>
            </td>
            <td>
                {transaction.linked_recurring_series_name ? (
                    <span style={{ color: '#10b981', fontSize: '0.85rem' }}>
                        {transaction.linked_recurring_series_name}
                    </span>
                ) : (
                    <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.85rem' }}>None</span>
                )}
            </td>
            <td>
                {transaction.linked_debt_name ? (
                    <div style={{ display:'flex', flexDirection:'column' }}>
                        <span style={{ color: '#fbbf24', fontSize: '0.85rem' }}>{transaction.linked_debt_name}</span>
                        {transaction.linked_debt_payment_id && (
                            <small style={{ color:'#9ca3af', fontSize:'0.7rem' }}>Payment ID: {transaction.linked_debt_payment_id}</small>
                        )}
                    </div>
                ) : (
                    <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.85rem' }}>None</span>
                )}
            </td>
            <td>
                {transaction.has_receipt ? (
                    <button
                        className="btn btn-link btn-sm p-0 text-info"
                        onClick={() => openReceipt(transaction.id, transaction.description)}
                    >
                        View Receipt
                    </button>
                ) : (
                    <span className="text-muted small">No Receipt</span>
                )}
            </td>
            <td>
                <div className="dropdown">
                    <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                        Actions
                    </button>
                    <ul className="dropdown-menu dropdown-menu-dark">
                        {/* 1. Category-specific context actions */}
                        {(() => {
                            const cat = String(transaction.expense_category || '');
                            const isIncome = cat === 'income' || cat.startsWith('income_');
                            const isDebt = cat === 'debt_payment' || cat.includes('financing') || cat.includes('interest');

                            if (isIncome) {
                                return (
                                    <>
                                        <li>
                                            <button className="dropdown-item" onClick={() => {
                                                setIncomeSplitRow({
                                                    id: transaction.id,
                                                    amount: transaction.amount,
                                                    date: transaction.transaction_date,
                                                    description: transaction.description,
                                                    sourceType: 'revolver'
                                                });
                                                setIncomeSplitDefaultKind('recurring');
                                                setShowIncomeSplitModal(true);
                                            }}>
                                                Link to Income Stream
                                            </button>
                                        </li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => {
                                                setIncomeLinkRow({
                                                    id: transaction.id,
                                                    amount: transaction.amount,
                                                    date: transaction.transaction_date,
                                                    description: transaction.description,
                                                    sourceType: 'revolver'
                                                });
                                                setShowIncomeLinkModal(true);
                                            }}>
                                                Link as General Income
                                            </button>
                                        </li>
                                    </>
                                );
                            } else if (isDebt) {
                                return (
                                    <>
                                        <li>
                                            <button className="dropdown-item" onClick={() => {
                                                setDebtDisbursementRow({
                                                    id: transaction.id,
                                                    amount: transaction.amount,
                                                    date: transaction.transaction_date,
                                                    description: transaction.description,
                                                    sourceType: 'revolver'
                                                });
                                                setShowDebtDisbursementModal(true);
                                            }}>
                                                Link to Debt Disbursement
                                            </button>
                                        </li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => {
                                                setActivePaymentTxnId(transaction.id);
                                                setShowPaymentModal(true);
                                            }}>
                                                Link as Debt Payment
                                            </button>
                                        </li>
                                    </>
                                );
                            }
                            return null;
                        })()}

                        {/* 2. Generic actions */}
                        <li>
                            <button className="dropdown-item" onClick={() => {
                                setSplitRow({
                                    id: transaction.id,
                                    sourceType: 'revolver',
                                    amount: transaction.amount,
                                    description: transaction.description,
                                    date: transaction.transaction_date
                                });
                                setShowSplitModal(true);
                            }}>
                                Split Transaction
                            </button>
                        </li>
                        <li>
                            {duplicateTransaction && (
                                <button className="dropdown-item" onClick={() => duplicateTransaction(transaction.id)}>
                                    Duplicate
                                </button>
                            )}
                        </li>
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                            <button className="dropdown-item text-danger" onClick={() => deleteTransaction(transaction.id)}>
                                Delete
                            </button>
                        </li>
                    </ul>
                </div>
            </td>
        </tr>
    );
});

const MemoizedTransactionTable = React.memo(({
    processedTransactions,
    id,
    deleting,
    expandedTxns,
    editingCell,
    saving,
    formatDate,
    formatCurrency,
    handleSort,
    SortIcon,
    startEditing,
    cancelEditing,
    saveEdit,
    setExpandedTxns,
    renderExpenseCategory,
    buildAssetLabel,
    getDomainForCategory,
    setUtilityRow,
    setShowUtilityModal,
    setShowSplitModal,
    setSplitRow,
    deleteTransaction,
    openReceipt,
    setShowIncomeSplitModal,
    setIncomeSplitRow,
    setIncomeSplitDefaultKind,
    setShowIncomeLinkModal,
    setIncomeLinkRow,
    setShowDebtDisbursementModal,
    setDebtDisbursementRow,
    setActivePaymentTxnId,
    setShowPaymentModal,
    duplicateTransaction
}) => {
    return (
        <table>
            <thead>
                <tr>
                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('transaction_date')}>
                        Transaction Date <SortIcon field="transaction_date"/>
                    </th>
                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('post_date')}>
                        Post Date <SortIcon field="post_date"/>
                    </th>
                    {/* Card column - only show for "All Cards" view */}
                    {id === 'all' && (
                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('card_display')}>
                            Card <SortIcon field="card_display"/>
                        </th>
                    )}
                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('description')}>
                        Description <SortIcon field="description"/>
                    </th>
                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('user_description')}>
                        User Description <SortIcon field="user_description"/>
                    </th>
                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('amount')}>
                        Amount <SortIcon field="amount"/>
                    </th>
                    <th scope="col" style={{ cursor: 'pointer', minWidth:'220px', whiteSpace:'nowrap' }} onClick={() => handleSort('expense_category')}>
                        Category <SortIcon field="expense_category"/>
                    </th>
                    <th scope="col">Asset Link</th>
                    <th scope="col">Linked To-Do</th>
                    <th scope="col">Recurring Series</th>
                    <th scope="col">Debt Link</th>
                    <th scope="col">Receipt</th>
                    <th scope="col">Actions</th>
                </tr>
            </thead>
            <tbody>
                {processedTransactions.length > 0 ? (
                    processedTransactions.map((transaction) => (
                        <TransactionRow
                            key={transaction.id}
                            transaction={transaction}
                            id={id}
                            deleting={deleting}
                            expandedTxns={expandedTxns}
                            editingCell={editingCell}
                            saving={saving}
                            formatDate={formatDate}
                            formatCurrency={formatCurrency}
                            startEditing={startEditing}
                            cancelEditing={cancelEditing}
                            saveEdit={saveEdit}
                            setExpandedTxns={setExpandedTxns}
                            renderExpenseCategory={renderExpenseCategory}
                            buildAssetLabel={buildAssetLabel}
                            getDomainForCategory={getDomainForCategory}
                            setUtilityRow={setUtilityRow}
                            setShowUtilityModal={setShowUtilityModal}
                            setShowSplitModal={setShowSplitModal}
                            setSplitRow={setSplitRow}
                            deleteTransaction={deleteTransaction}
                            openReceipt={openReceipt}
                            setShowIncomeSplitModal={setShowIncomeSplitModal}
                            setIncomeSplitRow={setIncomeSplitRow}
                            setIncomeSplitDefaultKind={setIncomeSplitDefaultKind}
                            setShowIncomeLinkModal={setShowIncomeLinkModal}
                            setIncomeLinkRow={setIncomeLinkRow}
                            setShowDebtDisbursementModal={setShowDebtDisbursementModal}
                            setDebtDisbursementRow={setDebtDisbursementRow}
                            setActivePaymentTxnId={setActivePaymentTxnId}
                            setShowPaymentModal={setShowPaymentModal}
                            duplicateTransaction={duplicateTransaction}
                        />
                    ))
                ) : (
                    <tr>
                        <td colSpan={id === 'all' ? 13 : 12} className="text-center py-4">
                            No transactions found for this statement.
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
});

const SplitTransactionModal = ({ isOpen, transaction, onClose, onSaveSuccess, formatCurrency }) => {
    const [splitItems, setSplitItems] = useState([]);
    const [splitError, setSplitError] = useState('');
    const [splitSubmitting, setSplitSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && transaction) {
            const absAmt = Math.abs(Number(transaction.amount) || 0);
            const initial = absAmt > 0 ? [
                { key: 1, amount: Math.round((absAmt/2) * 100)/100, description: transaction.description },
                { key: 2, amount: Math.round((absAmt - Math.round((absAmt/2) * 100)/100) * 100)/100, description: transaction.description },
            ] : [{ key: 1, amount: 0, description: transaction.description }, { key: 2, amount: 0, description: transaction.description }];
            setSplitItems(initial);
            setSplitError('');
        }
    }, [isOpen, transaction]);

    if (!isOpen || !transaction) return null;

    const handleSave = async () => {
        setSplitError('');
        const parentAbs = Math.abs(Number(transaction.amount) || 0);
        const total = splitItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
        
        if (Math.abs(total - parentAbs) > 0.01) {
            setSplitError('Split amounts must sum exactly to the parent amount.');
            return;
        }
        if (splitItems.length < 2) {
            setSplitError('Add at least two split rows.');
            return;
        }

        setSplitSubmitting(true);
        try {
            const url = `/balance_sheet/financing/revolvers/transactions/${transaction.id}/split/`;
            const payload = { 
                splits: splitItems.map(it => ({ 
                    amount: parseFloat(it.amount) || 0, 
                    description: (it.description || '').trim() || null 
                })) 
            };
            await api.post(url, payload);
            onSaveSuccess();
        } catch (e) {
            setSplitError('Failed to create split.');
        } finally {
            setSplitSubmitting(false);
        }
    };

    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }} onClick={()=> { if (!splitSubmitting) onClose(); }}>
            <div style={{ width:'min(720px, 95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }} onClick={(e)=> e.stopPropagation()}>
                <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                    <div style={{ fontSize:18, fontWeight:700 }}>Split Transaction</div>
                    <button className="btn btn-sm btn-secondary" onClick={()=> { if (!splitSubmitting) onClose(); }} style={{ marginLeft:'auto' }}>✕</button>
                </div>
                <div style={{ padding:16 }}>
                    {splitError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{splitError}</div>}
                    <div style={{ marginBottom:8, color:'#9ca3af' }}>
                        <div><strong>Date:</strong> {transaction.transaction_date || transaction.date || ''}</div>
                        <div><strong>Description:</strong> {transaction.description || ''}</div>
                        <div><strong>Parent Amount:</strong> {formatCurrency(Math.abs(Number(transaction.amount)||0))}</div>
                        <small>Enter positive amounts for each child. They must sum to the parent amount.</small>
                    </div>
                    <table style={{ width:'100%' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign:'left', padding:8 }}>Child</th>
                                <th style={{ textAlign:'right', padding:8, width:160 }}>Amount</th>
                                <th style={{ textAlign:'left', padding:8 }}>Description</th>
                                <th style={{ textAlign:'left', padding:8 }}>Remove</th>
                            </tr>
                        </thead>
                        <tbody>
                            {splitItems.map((it, idx) => (
                                <tr key={it.key}>
                                    <td style={{ padding:8 }}>#{idx+1}</td>
                                    <td style={{ padding:8, textAlign:'right' }}>
                                        <input type="number" step="0.01" className="form-control form-control-sm" value={it.amount}
                                            onChange={(e)=> {
                                                const v = e.target.value;
                                                setSplitItems(prev => prev.map(x => x.key===it.key ? { ...x, amount: v } : x));
                                            }} />
                                    </td>
                                    <td style={{ padding:8 }}>
                                        <input type="text" className="form-control form-control-sm" value={it.description || ''}
                                            onChange={(e)=> setSplitItems(prev => prev.map(x => x.key===it.key ? { ...x, description: e.target.value } : x))} />
                                    </td>
                                    <td style={{ padding:8 }}>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={()=> setSplitItems(prev => prev.filter(x => x.key !== it.key))} disabled={splitItems.length <= 2 || splitSubmitting}>Remove</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="d-flex justify-content-between align-items-center" style={{ marginTop:12 }}>
                        <button className="btn btn-outline-secondary btn-sm" onClick={()=> setSplitItems(prev => [...prev, { key: (prev[prev.length-1]?.key||0)+1, amount: 0, description: transaction.description }])} disabled={splitSubmitting}>+ Add Split</button>
                        <div style={{ color:'#9ca3af' }}>
                            Sum: {formatCurrency((splitItems.reduce((s,it)=> s + (parseFloat(it.amount)||0), 0)))} of {formatCurrency(Math.abs(Number(transaction.amount)||0))}
                        </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                        <button className="btn btn-secondary" onClick={onClose} disabled={splitSubmitting}>Cancel</button>
                        <button className="btn btn-success" disabled={splitSubmitting} onClick={handleSave}>{splitSubmitting ? 'Saving…' : 'Save Split'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CreditCardDetail = () => {
    const { id } = useParams();  // Get credit card ID from the URL (or "all")
    const navigate = useNavigate();

    const [creditCard, setCreditCard] = useState(null);
    const [allCreditCards, setAllCreditCards] = useState([]); // For navigation tabs
    const [statementSummaryData, setstatementSummaryData] = useState(null);
    const [rawTransactions, setRawTransactions] = useState([]); // Store raw API data
    const [statements, setStatements] = useState([]);
    const [selectedStatement, setSelectedStatement] = useState(null);
    const [expenseCategories, setExpenseCategories] = useState({ items: [], noneItem: { value: 'none', label: 'None' } });
    const [expandedParents, setExpandedParents] = useState({}); // { [dropdownId]: Set(parentValue) }
    const [expandedTxns, setExpandedTxns] = useState(new Set());
    const [openDropdownId, setOpenDropdownId] = useState(null); // Track which dropdown is open
    const [loading, setLoading] = useState(true);
    const [switchingCard, setSwitchingCard] = useState(false);
    const [isAllCardsView, setIsAllCardsView] = useState(id === 'all'); // Track if showing all cards

    // Sorting state
    const [sortField, setSortField] = useState('transaction_date');
    const [sortDirection, setSortDirection] = useState('desc');

    // Editing state
    const [editingCell, setEditingCell] = useState(null); // { transactionId, field }
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);

    // New transaction modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [newTransaction, setNewTransaction] = useState({
        transaction_date: '',
        post_date: '',
        description: '',
        amount: '',
        expense_category: 'none'
    });
    const [addingTransaction, setAddingTransaction] = useState(false);

    // Deletion state
    const [deleting, setDeleting] = useState(new Set()); // Track which transactions are being deleted
    const [showSplitModal, setShowSplitModal] = useState(false);
    const [splitRow, setSplitRow] = useState(null); // { id, sourceType, amount, description, date }
    const [showUtilityModal, setShowUtilityModal] = useState(false);
    const [utilityRow, setUtilityRow] = useState(null);

    // Income link modal state
    const [showIncomeLinkModal, setShowIncomeLinkModal] = useState(false);
    const [showDebtDisbursementModal, setShowDebtDisbursementModal] = useState(false);
    const [debtDisbursementRow, setDebtDisbursementRow] = useState(null);
    const [incomeLinkRow, setIncomeLinkRow] = useState(null);

    // Income split modal state
    const [showIncomeSplitModal, setShowIncomeSplitModal] = useState(false);
    const [incomeSplitRow, setIncomeSplitRow] = useState(null); // { id, amount, date, description, sourceType }
    const [incomeSplitDefaultKind, setIncomeSplitDefaultKind] = useState('recurring');
    const [error, setError] = useState(null);

    const [autoCategorizedIds, setAutoCategorizedIds] = useState(new Set());
    const autoCategorizedIdsRef = React.useRef(new Set()); // Ref so autoAssignCategories always has latest without being a fetchTransactions dep

    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [activePaymentTxnId, setActivePaymentTxnId] = useState(null);
    const [debtsForPayment, setDebtsForPayment] = useState([]);
    const [selectedDebt, setSelectedDebt] = useState({ type: '', id: '' });
    const [paymentCandidates, setPaymentCandidates] = useState([]);
    const [selectedCandidateId, setSelectedCandidateId] = useState('');
    const [loadingPayment, setLoadingPayment] = useState(false);

    const apiBase = '/balance_sheet/financing/revolvers';

    // --- Optimization: Pre-calculate category label map ---
    const categoryLabelMap = useMemo(() => {
        const map = new Map();
        if (expenseCategories.items) {
            expenseCategories.items.forEach(it => {
                map.set(String(it.value), it.label);
                if (it.children) {
                    it.children.forEach(ch => map.set(String(ch.value), ch.label));
                }
            });
        }
        if (expenseCategories.noneItem) {
            map.set(String(expenseCategories.noneItem.value), expenseCategories.noneItem.label);
        }
        return map;
    }, [expenseCategories]);

    // Build a fast lookup for credit cards by id (for All Cards view)
    const cardMap = useMemo(() => {
        const map = new Map();
        if (Array.isArray(allCreditCards)) {
            for (const c of allCreditCards) {
                if (c && (typeof c.id === 'number' || typeof c.id === 'string')) {
                    map.set(String(c.id), c);
                }
            }
        }
        return map;
    }, [allCreditCards]);

    // Helper: auto-assign categories for new/uncategorized transactions based on most recent prior with same description
    const autoAssignCategories = async (transactions) => {
        try {
            if (!Array.isArray(transactions) || id === 'all') return; // Only within a single card
            const getTime = (t) => {
                const s = t?.post_date || t?.transaction_date;
                const d = s ? new Date(s) : null;
                return d && !isNaN(d.getTime()) ? d.getTime() : 0;
            };
            const sorted = [...transactions].sort((a, b) => (getTime(a) - getTime(b)) || (Number(a.id) - Number(b.id)));
            const lastByDesc = new Map();
            for (const t of sorted) {
                const desc = t?.description || '';
                if (!desc) continue;
                const currentCat = t?.expense_category || 'none';
                if (currentCat && currentCat !== 'none') {
                    lastByDesc.set(desc, currentCat);
                    continue;
                }
                const priorCat = lastByDesc.get(desc);
                if (priorCat && !autoCategorizedIdsRef.current.has(t.id)) {
                    try {
                        await api.patch(`/balance_sheet/financing/revolvers/transactions/${t.id}/update_expense_category/`, { expense_category: priorCat });
                        setRawTransactions(prev => prev.map(x => x.id === t.id ? { ...x, expense_category: priorCat } : x));
                        autoCategorizedIdsRef.current.add(t.id);
                        setAutoCategorizedIds(new Set(autoCategorizedIdsRef.current));
                    } catch (e) {
                        // ignore individual failures
                    }
                }
            }
        } catch (e) {
            // ignore batch failure
        }
    };

    // Fetch transactions based on selected statement - modified for all cards
    const fetchTransactions = useCallback(async () => {
        if (!id) return;
        if (selectedStatement === null) return; // Wait until default is determined

        try {
            let url;
            if (id === 'all') {
                // For "All Cards" view, pass the selected statement if it exists
                url = `/balance_sheet/financing/revolvers/transactions/by-all-credit-cards/`;
                if (selectedStatement) {
                    url += `?statement_id=${selectedStatement}`;
                }
                console.log('All Cards view: fetching transactions. Statement ID:', selectedStatement || 'Latest');
            } else {
                // Individual card (existing logic)
                url = selectedStatement
                    ? `/balance_sheet/financing/revolvers/transactions/by-revolver/${id}/?statement_id=${selectedStatement}`
                    : `/balance_sheet/financing/revolvers/transactions/by-revolver/${id}/`;
            }

            console.log('Fetching transactions from URL:', url);
            const response = await api.get(url);

            // Ensure we're working with unique transactions by ID
            const uniqueTransactions = [];
            const seenIds = new Set();
            const list = Array.isArray(response.data) ? response.data : (response.data.transactions || []);

            for (const transaction of list) {
                if (!seenIds.has(transaction.id)) {
                    seenIds.add(transaction.id);
                    // Add card info for display in table when viewing all cards
                    if (id === 'all') {
                        // Use shared resolver for consistency and easier testing
                        transaction.card_display = resolveCardDisplay(transaction, cardMap);
                    }
                    uniqueTransactions.push(transaction);
                }
            }

            setRawTransactions(uniqueTransactions);
            console.log('Fetched transactions:', uniqueTransactions.length);
            // Auto-assign categories for uncategorized transactions based on most recent prior with same description
            try { await autoAssignCategories(uniqueTransactions); } catch(e) { console.warn('Auto-assign categories failed', e); }
        } catch (error) {
            console.error('Error fetching transactions:', error);
        }
    }, [id, selectedStatement, cardMap]); // autoCategorizedIds intentionally excluded — it's only used in autoAssignCategories post-fetch and must not cause re-fetch loops

    const {
      showReceiptModal,
      receiptPreviewUrl,
      receiptModalTitle,
      openReceipt,
      closeReceipt,
      uploadReceipt,
      deleteReceipt,
    } = useReceipts('/balance_sheet/financing/revolvers/transactions', fetchTransactions);

    const apiEndpoint = '/balance_sheet/financing/revolvers/credit-cards/upload_pdf_statements/';
    const excelApiEndpoint = '/balance_sheet/financing/revolvers/credit-cards/upload_excel_statements/';



    // Fetch all credit cards for navigation
    useEffect(() => {
        const fetchAllCreditCards = async () => {
            try {
                const response = await api.get('/balance_sheet/financing/revolvers/credit-cards/');
                setAllCreditCards(response.data);
            } catch (error) {
                console.error('Error fetching all credit cards:', error);
            }
        };

        fetchAllCreditCards();
    }, []);

    // Update isAllCardsView when id changes
    useEffect(() => {
        setIsAllCardsView(id === 'all');
    }, [id]);

    // Fetch credit card data - modified to handle "all" case
    useEffect(() => {
        const fetchCreditCardData = async () => {
            console.log('Fetching credit card data for id:', id); // Debug log

            if (id === 'all') {
                console.log('Setting up All Cards view'); // Debug log
                setCreditCard({
                    lender: 'All Credit Cards',
                    last_four_digits_of_card: '',
                    id: 'all'
                });
                setLoading(false);
                setSwitchingCard(false);
                return;
            }

            try {
                const response = await api.get(`/balance_sheet/financing/revolvers/credit-cards/${id}/`);
                console.log(response.data)
                setCreditCard(response.data);
            } catch (error) {
                console.error('Error fetching credit card details:', error);
                setError('Failed to fetch credit card details.');
            } finally {
                setLoading(false);
                setSwitchingCard(false);
            }
        };

        if (id) {
            fetchCreditCardData();
        } else {
            console.log('No id parameter found'); // Debug log
        }
    }, [id]);

    // Fetch statement summary data - modified to handle aggregation
    const fetchStatementSummaryData = useCallback(async (loadAll = false) => {
        // console.log('fetchStatementSummaryData called with:', { id, isAllCardsView: id === 'all', loadAll }); // Debug log

        try {
            let url;
            if (id === 'all') {  // Use id directly instead of isAllCardsView state
                // Use the aggregated endpoint that already exists
                url = '/balance_sheet/financing/revolvers/credit-cards/statement-summary-data/';
                console.log('Using aggregated endpoint:', url);
            } else {
                url = `/balance_sheet/financing/revolvers/${id}/statement-summary-data/`;
                if (!loadAll) {
                    url += '?latest_only=true';
                }
                console.log('Using individual card endpoint:', url);
            }

            const response = await api.get(url);
            console.log('Statement summary data response:', response.data);

            if (id === 'all') {  // Use id directly instead of isAllCardsView state
                // Process aggregated data like in RevolverCreditCardsPage.js
                const grouped = {};

                response.data.forEach(item => {
                    const year = item.statement_year_int;
                    const month = item.statement_month_int;
                    const key = `${year}-${month}`;

                    if (!grouped[key]) {
                        const monthNames = [
                            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                        ];
                        const label = `${monthNames[month - 1]} ${year}`;

                        grouped[key] = {
                            label,
                            year,
                            month,
                            billing_cycle_end_date: item.billing_cycle_end_date,
                            billing_cycle_start_date: item.billing_cycle_start_date,
                            id: key, // Use combined key as ID
                            statement_month_int: month,
                            statement_year_int: year,
                            previous_balance: 0,
                            transactions: 0,
                            interest_charged: 0,
                            payments: 0,
                            balance_subject_to_interest_rate: 0,
                            new_balance: 0,
                            has_transactions: false,
                        };
                    }

                    // Aggregate the values
                    grouped[key].previous_balance += Number(item.previous_balance) || 0;
                    grouped[key].transactions += Number(item.transactions) || 0;
                    grouped[key].interest_charged += Number(item.interest_charged) || 0;
                    const payPlusCred = (Number(item.payments) || 0) + (Number(item.other_credits) || 0);
                    grouped[key].payments += payPlusCred;
                    grouped[key].balance_subject_to_interest_rate += Number(item.balance_subject_to_interest_rate) || 0;
                    grouped[key].new_balance += Number(item.new_balance) || 0;
                    if (item.has_transactions) {
                        grouped[key].has_transactions = true;
                    }
                });

                // Sort chronologically
                const sortedKeys = Object.keys(grouped).sort((a, b) => {
                    const gA = grouped[a];
                    const gB = grouped[b];
                    if (gA.year !== gB.year) return gA.year - gB.year;
                    return gA.month - gB.month;
                });

                const chartDataArray = sortedKeys.map(key => ({
                    period: grouped[key].label,
                    previous_balance: grouped[key].previous_balance,
                    transactions: grouped[key].transactions,
                    interest_charged: grouped[key].interest_charged,
                    payments: grouped[key].payments,
                    balance_subject_to_interest_rate: grouped[key].balance_subject_to_interest_rate,
                    new_balance: grouped[key].new_balance,
                }));

                console.log('Processed aggregated chart data:', chartDataArray);
                setstatementSummaryData(chartDataArray);

                // Set statements for dropdown (most recent first)
                const statementsList = sortedKeys.map(key => grouped[key]).reverse();
                setStatements(statementsList);
                // For all-cards view, default to empty (no filter) so fetchTransactions can proceed
                setSelectedStatement(prev => prev === null ? '' : prev);

            } else {
                // Individual card logic (existing)
                console.log('Processing individual card data');
                if (loadAll) {
                    const data = Array.isArray(response.data) ? response.data : [];
                    setstatementSummaryData(data);
                    const sortedStatements = [...data].sort((a, b) => {
                        return new Date(b.billing_cycle_end_date) - new Date(a.billing_cycle_end_date);
                    });
                    setStatements(sortedStatements);
                } else {
                    const data = Array.isArray(response.data) ? response.data : [];
                    setstatementSummaryData(data);
                    setStatements(data);
                    // Auto-select default: 'current' if there are post-statement transactions, else latest statement
                    try {
                        const currentResp = await api.get(`/balance_sheet/financing/revolvers/transactions/by-revolver/${id}/?statement_id=current`);
                        const currentList = Array.isArray(currentResp.data) ? currentResp.data : (currentResp.data.transactions || []);
                        if (currentList.length > 0) {
                            setSelectedStatement('current');
                        } else {
                            const sortedData = [...data].sort((a, b) => new Date(b.billing_cycle_end_date) - new Date(a.billing_cycle_end_date));
                            if (sortedData.length > 0) {
                                setSelectedStatement(String(sortedData[0].id));
                            }
                        }
                    } catch (e) {
                        // Fallback: default to latest statement
                        const sortedData = [...data].sort((a, b) => new Date(b.billing_cycle_end_date) - new Date(a.billing_cycle_end_date));
                        if (sortedData.length > 0) {
                            setSelectedStatement(String(sortedData[0].id));
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching statement summary:', error);
        }
    }, [id]);

    // Handle statement dropdown change - load all data if not already loaded
    const handleStatementChange = async (statementId) => {
        setSelectedStatement(statementId);

        // If we haven't loaded all statements yet and user is selecting a specific (non-current) statement
        if (statementId && statementId !== 'current' && statements.length <= 1) {
            await fetchStatementSummaryData(true); // Load all statement data
        }
    };

    // Make sure to call fetchStatementSummaryData when id changes and clear previous data
    useEffect(() => {
        if (id) {
            // Clear previous data to prevent stale data issues
            setstatementSummaryData(null);
            setStatements([]);
            // Don't reset selectedStatement here — fetchStatementSummaryData will set the correct default

            fetchStatementSummaryData(false); // Initially load only latest
        }
    }, [id]);

    // Asset list for resolving linked asset names/URLs
    const [allAssets, setAllAssets] = useState([]);
    useEffect(() => {
        const loadAssets = async () => {
            try {
                const phys = await api.get('/balance_sheet/assets/physical_assets/');
                setAllAssets(Array.isArray(phys.data) ? phys.data : []);
            } catch (e) {
                setAllAssets([]);
            }
        };
        loadAssets();
    }, []);
    const buildAssetUrl = (asset) => {
        if (!asset) return null;
        const id = asset.id;
        const type2 = asset.asset_type_2 || asset.type_2 || asset.type2;
        const t = (type2 || '').toLowerCase();
        if (t === 'vehicle') return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
        if (t === 'resi_property') return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
        if (t === 'structure' || t === 'land') {
          const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent;
          const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
          if (rpId) return `/balance_sheet/assets/physical_assets/resi_properties/${rpId}`;
        }
        return null;
      };

  // Memoized helper maps for fast asset lookups and labels
  const assetMaps = useMemo(() => {
    try {
      const list = Array.isArray(allAssets) ? allAssets : [];
      const byId = new Map();
      const resiAddressById = new Map(); // property id -> street address
      const structIdsByProperty = new Map(); // property id -> sorted structure ids
      for (const a of list) {
        const idStr = String(a.id);
        byId.set(idStr, a);
        const type2 = (a.asset_type_2 || a.type_2 || a.type2 || '').toLowerCase();
        if (type2 === 'resi_property') {
          const addr = a.address && a.address.street_address ? a.address.street_address : '';
          resiAddressById.set(idStr, addr);
        } else if (type2 === 'structure') {
          const rp = a.residential_property || a.residentialProperty || a.parent_property || a.parent || null;
          const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
          if (rpId != null) {
            const key = String(rpId);
            if (!structIdsByProperty.has(key)) structIdsByProperty.set(key, []);
            structIdsByProperty.get(key).push(a.id);
          }
        }
      }
      // Sort structure ids for ordinal positions
      const structOrdinalById = new Map();
      for (const ids of structIdsByProperty.values()) {
        ids.sort((a,b) => a - b);
        ids.forEach((sid, idx) => {
          structOrdinalById.set(String(sid), idx + 1);
        });
      }
      return { byId, resiAddressById, structOrdinalById };
    } catch (e) {
      return { byId: new Map(), resiAddressById: new Map(), structOrdinalById: new Map() };
    }
  }, [allAssets]);

  const buildStructureLabel = (asset) => buildAssetLabel(asset, assetMaps);

    // Fetch expense category choices
    useEffect(() => {
        const fetchExpenseCategories = async () => {
            try {
                const response = await api.get('/balance_sheet/financing/revolvers/expense-category-choices/');
                const tree = buildCategoryTreeSorted(response.data || []);
                setExpenseCategories(tree);
            } catch (error) {
                console.error('Error fetching expense categories:', error);
            }
        };

        fetchExpenseCategories();
    }, []);

    useEffect(() => {
        // In All Cards view, wait until allCreditCards are loaded to ensure proper lender/last4 resolution
        if (id === 'all') {
            if (allCreditCards && allCreditCards.length > 0) {
                fetchTransactions();
            }
        } else {
            fetchTransactions();
        }
    }, [id, selectedStatement, allCreditCards, fetchTransactions]);

    // Handle credit card switching - modified to support "all"
    const handleCreditCardSwitch = (creditCardId) => {
        if (creditCardId !== id) {
            setSwitchingCard(true);
            setSelectedStatement(null); // Reset statement filter — will be re-set by fetchStatementSummaryData
            setRawTransactions([]); // Clear current transactions

            if (creditCardId === 'all') {
                navigate(`/balance_sheet/financing/revolvers/credit-cards/all/`);
            } else {
                navigate(`/balance_sheet/financing/revolvers/credit-cards/${creditCardId}/`);
            }
        }
    };

    // Filter and sort transactions - this creates the base dataset
    const sortedBaseTransactions = useMemo(() => {
        if (!rawTransactions || rawTransactions.length === 0) return [];

        const data = rawTransactions.map(t => {
            const asset = t.linked_asset_id ? (allAssets || []).find(a => String(a.id) === String(t.linked_asset_id)) : null;
            const assetLabel = asset ? buildAssetLabel(asset, { ...assetMaps, transactionDomain: getDomainForCategory(t.expense_category) }) : '';

            let debtLabel = '';
            if (t.linkedDebt && (t.linkedDebt.type === 'auto_loan' || t.linkedDebt.type === 'mortgage') && t.linkedDebtId) {
                debtLabel = t.linkedDebt.debt_label || t.linkedDebt.debt_name || 'Debt';
            }

            return {
                ...t,
                linked_asset_label: assetLabel,
                linked_debt_label: debtLabel
            };
        });

        // The API already filters by statement, so we just need to sort
        return [...data].sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            // Handle different field types
            if (sortField === 'amount') {
                aValue = parseFloat(aValue);
                bValue = parseFloat(bValue);
            } else if (sortField === 'transaction_date' || sortField === 'post_date') {
                aValue = aValue ? new Date(aValue) : null;
                bValue = bValue ? new Date(bValue) : null;
            } else if (sortField === 'expense_category') {
                // Get the label for sorting
                const getCategoryLabel = (category) => {
                    for (const it of expenseCategories.items) {
                        if (String(it.value) === String(category || 'none')) return it.label;
                        if (Array.isArray(it.children)) {
                            const ch = it.children.find(c => String(c.value) === String(category || 'none'));
                            if (ch) return ch.label;
                        }
                    }
                    if (String(expenseCategories.noneItem?.value) === String(category || 'none')) return expenseCategories.noneItem?.label || 'None';
                    return 'None';
                };
                aValue = getCategoryLabel(aValue);
                bValue = getCategoryLabel(bValue);
            } else if (sortField === 'card_display') {
                // Handle card_display sorting
                aValue = aValue || '';
                bValue = bValue || '';
            } else if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            // Handle null/undefined values
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            // Sort comparison
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [rawTransactions, sortField, sortDirection, expenseCategories, allAssets, assetMaps]);

    // Hierarchical table rows: parents only at top-level, children on expand
    const hierarchy = useMemo(() => {
        const byId = new Map();
        const childrenByParent = new Map();
        const childIds = new Set();
        for (const t of (sortedBaseTransactions || [])) {
            byId.set(t.id, t);
        }
        for (const t of (sortedBaseTransactions || [])) {
            const p = t.parent_id || t.parent;
            if (p) {
                if (!childrenByParent.has(p)) childrenByParent.set(p, []);
                childrenByParent.get(p).push(t);
                childIds.add(t.id);
            }
        }
        // Ensure children are also sorted (usually they match parent order but good to be explicit)
        for (const [, arr] of childrenByParent.entries()) {
            arr.sort((a,b) => {
                const ad = a.post_date || a.transaction_date;
                const bd = b.post_date || b.transaction_date;
                if (ad === bd) return a.id - b.id;
                return (ad < bd) ? -1 : 1;
            });
        }
        return { byId, childrenByParent, childIds };
    }, [sortedBaseTransactions]);

    const processedTransactions = useMemo(() => {
        const out = [];
        const parentsOnly = sortedBaseTransactions.filter(r => !hierarchy.childIds.has(r.id));

        for (const p of parentsOnly) {
            out.push({
                ...p,
                _isChild: false,
                has_children: !!(p.children_count || (hierarchy.childrenByParent.get(p.id) || []).length > 0 || (Array.isArray(p.children) && p.children.length > 0))
            });

            if (expandedTxns.has(p.id)) {
                const kids = hierarchy.childrenByParent.get(p.id) || [];
                for (const kt of kids) {
                    out.push({ ...kt, _isChild: true, has_children: false });
                }
                // If no real children found in main list, check for synthetic children from API
                if (kids.length === 0 && Array.isArray(p.children) && p.children.length > 0) {
                    for (const sc of p.children) {
                        out.push({
                            ...sc,
                            id: `synth-${p.id}-${sc.id}`,
                            transaction_date: p.transaction_date,
                            post_date: p.post_date,
                            description: sc.description || p.description,
                            amount: sc.amount,
                            expense_category: sc.expense_category,
                            linked_asset_id: sc.linked_asset_id || sc.asset_id || sc.asset || '',
                            linked_asset_expense_id: sc.linked_asset_expense_id || sc.id || '',
                            linked_utility_bill_id: sc.linked_utility_bill_id || sc.utility_bill_id || sc.utility_bill || '',
                            linked_recurring_series_id: sc.linked_recurring_series_id || sc.recurring_series_id || sc.recurring_series || '',
                            _isChild: true,
                            has_children: false,
                            parent_id: p.id
                        });
                    }
                }
            }
        }
        return out;
    }, [sortedBaseTransactions, hierarchy, expandedTxns]);

    // Transactions for the pie chart - should only include independent transactions and children (exclude parents)
    const chartTransactions = useMemo(() => {
        // A transaction is a parent if it has children in the hierarchy OR if it has a non-zero children_count/children array
        return sortedBaseTransactions.filter(t => {
            const isChild = hierarchy.childIds.has(t.id);
            const hasKids = !!(t.children_count || (hierarchy.childrenByParent.get(t.id) || []).length > 0 || (Array.isArray(t.children) && t.children.length > 0));
            
            // Include it if it's a child OR if it's an independent transaction (not a child and has no kids)
            return isChild || !hasKids;
        });
    }, [sortedBaseTransactions, hierarchy]);

    // Close dropdown and editing when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setOpenDropdownId(null);
            if (editingCell && !saving) {
                setEditingCell(null);
                setEditValue('');
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [editingCell, saving]);

    // Handle sort
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const ExcelIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
    );

    const handleExport = () => {
        const columns = [
            { header: 'Date', key: t => t.transaction_date ? new Date(t.transaction_date).toLocaleDateString() : '' },
            { header: 'Post Date', key: t => t.post_date ? new Date(t.post_date).toLocaleDateString() : '' },
        ];
        
        if (id === 'all') {
            columns.push({ header: 'Card', key: t => t.card_display || '' });
        }
        
        columns.push(
            { header: 'Description', key: 'description' },
            { header: 'User Description', key: 'user_description' },
            { header: 'Amount', key: 'amount' },
            { header: 'Category', key: 'expense_category' },
            { header: 'Asset Link', key: t => t.linked_asset_label || '' },
            { header: 'Debt Link', key: t => t.linked_debt_label || '' }
        );
        
        const accountName = id === 'all' ? 'All' : (creditCard ? (creditCard.account_name || creditCard.lender_name || creditCard.lender || 'CreditCard') : (id || 'CreditCard'));
        const lastFour = creditCard?.last_four_digits_of_card || '';
        const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_');
        const filename = `Transactions_${accountName}${lastFour ? '_' + lastFour : ''}_${dateStr}`;
        exportToExcel(processedTransactions, filename, columns);
    };

    // Sort icon component
    const SortIcon = ({ field }) => {
        if (sortField !== field) {
            return <span className="text-muted ms-1" style={{ cursor: 'pointer' }}>⇅</span>;
        }
        return (
            <span className="text-primary ms-1" style={{ cursor: 'pointer' }}>
                {sortDirection === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    // Start editing a cell
    const startEditing = useCallback((transactionId, field, currentValue) => {
        setEditingCell({ transactionId, field });

        // Format the value for editing based on field type
        if (field === 'transaction_date' || field === 'post_date') {
            // For canonical YYYY-MM-DD values, use as-is to avoid TZ shifts
            if (typeof currentValue === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.test(currentValue)) {
                setEditValue(currentValue);
            } else {
                const date = new Date(currentValue);
                setEditValue(isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0]);
            }
        } else if (field === 'amount') {
            // Remove currency formatting for editing
            setEditValue(currentValue.toString());
        } else {
            setEditValue(currentValue || '');
        }
    }, []);

    // Cancel editing
    const cancelEditing = useCallback(() => {
        setEditingCell(null);
        setEditValue('');
    }, []);

    // Save edited value
    const saveEdit = useCallback(async (overridingValue = null) => {
        if (!editingCell || saving) return;

        setSaving(true);

        try {
            // Validate input based on field type
            let valueToSave = overridingValue !== null ? overridingValue : editValue;

            if (editingCell.field === 'amount') {
                const numValue = parseFloat(valueToSave);
                if (isNaN(numValue)) {
                    alert('Please enter a valid number for amount');
                    setSaving(false);
                    return;
                }
                valueToSave = numValue;
            } else if (editingCell.field === 'transaction_date' || editingCell.field === 'post_date') {
                const dateValue = new Date(valueToSave);
                if (isNaN(dateValue.getTime())) {
                    alert('Please enter a valid date');
                    setSaving(false);
                    return;
                }
            } else if (editingCell.field === 'description') {
                if (typeof valueToSave !== 'string' || !valueToSave.trim()) {
                    alert('Description cannot be empty');
                    setSaving(false);
                    return;
                }
                valueToSave = valueToSave.trim();
            }

            // Make API call to update
            await api.patch(
                `/balance_sheet/financing/revolvers/transactions/${editingCell.transactionId}/`,
                { [editingCell.field]: valueToSave }
            );

            // Update local state
            setRawTransactions(prev => prev.map(transaction =>
                transaction.id === editingCell.transactionId
                    ? { ...transaction, [editingCell.field]: valueToSave }
                    : transaction
            ));

            // Clear editing state
            setEditingCell(null);
            setEditValue('');

            // If we updated post_date, amount, or description, refresh to show updated filtering/summary
            if (editingCell.field === 'post_date' || editingCell.field === 'amount' || editingCell.field === 'description') {
                await fetchTransactions();
                await fetchStatementSummaryData(true);
            }
        } catch (error) {
            console.error('Error saving edit:', error);
            alert('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    }, [editingCell, saving, editValue, fetchTransactions, fetchStatementSummaryData]);

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            saveEdit();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    };

    // Delete transaction
    const deleteTransaction = async (transactionId) => {
        if (!window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
            return;
        }

        setDeleting(prev => new Set([...prev, transactionId]));

        try {
            await api.delete(`/balance_sheet/financing/revolvers/transactions/${transactionId}/`);

            // Remove from local state
            setRawTransactions(prev => prev.filter(transaction => transaction.id !== transactionId));

            // Refresh data to update charts
            await fetchStatementSummaryData(statements.length > 1);

            alert('Transaction deleted successfully!');
        } catch (error) {
            console.error('Error deleting transaction:', error);
            alert('Failed to delete transaction. Please try again.');
        } finally {
            setDeleting(prev => {
                const newSet = new Set(prev);
                newSet.delete(transactionId);
                return newSet;
            });
        }
    };

    // Update expense category for a transaction
    // Landscaping modal state for revolver transactions
    const [showLandModal, setShowLandModal] = useState(false);
    const [landTxnId, setLandTxnId] = useState(null);
    const [properties, setProperties] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState('');
    const [landChild, setLandChild] = useState(null);
    const [landLoading, setLandLoading] = useState(false);
    const [landError, setLandError] = useState('');
    const [landIsRecurring, setLandIsRecurring] = useState(false);
    const [landFrequency, setLandFrequency] = useState('monthly');
    // Add Land form state
    const [showAddLand, setShowAddLand] = useState(false);
    const [landFormLoading, setLandFormLoading] = useState(false);
    const [landFormError, setLandFormError] = useState('');
    const [landForm, setLandForm] = useState({
        legal_description: '',
        parcel_number: '',
        lot_number: '',
        plat_number: '',
        tax_district: '',
        area_in_acres: '',
        latitude: '',
        longitude: '',
        survey_description: '',
        financing_allocation: ''
    });

    const [landRecurringOptions, setLandRecurringOptions] = useState([]);
    const [landRecurringLoading, setLandRecurringLoading] = useState(false);
    const [landRecurringChoice, setLandRecurringChoice] = useState(''); // existing id or '__new__'
    const [landRecurringName, setLandRecurringName] = useState('');

    const openLandModal = async (transactionId) => {
        setLandTxnId(transactionId);
        setShowLandModal(true);
        setSelectedPropertyId('');
        setLandChild(null);
        setLandError('');
        setLandLoading(true);
        try {
            const res = await api.get('/balance_sheet/assets/physical_assets/property-choices/');
            setProperties(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setProperties([]);
            setLandError('Failed to load properties.');
        } finally {
            setLandLoading(false);
        }
    };

    const onChangePropertyForLand = async (pid) => {
        setSelectedPropertyId(pid);
        setLandChild(null);
        if (!pid) return;
        setLandLoading(true);
        setLandError('');
        try {
            const opts = await api.get(`/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
            const children = (opts.data && Array.isArray(opts.data.children)) ? opts.data.children : [];
            const land = children.find(ch => (ch.type || '').toLowerCase() === 'land');
            setLandChild(land ? { id: land.id, label: land.label } : null);
        } catch (e) {
            setLandError('Failed to load property details.');
            setLandChild(null);
        } finally {
            setLandLoading(false);
        }
    };

    const fetchLandRecurringOptions = async (assetId) => {
        if (!assetId) return;
        try {
            setLandRecurringLoading(true);
            const res = await api.get(`/balance_sheet/assets/physical_assets/${assetId}/maintenance-items/`);
            const items = Array.isArray(res.data) ? res.data.map(m => ({ id: m.id, name: m.description || `Series ${m.id}`, frequency: m.recurring_frequency })) : [];
            setLandRecurringOptions(items);
        } catch (e) {
            setLandRecurringOptions([]);
        } finally {
            setLandRecurringLoading(false);
        }
    };

    const submitLandLink = async () => {
        if (!landTxnId || !landChild) { setLandError('Please select property with Land.'); return; }
        setLandLoading(true);
        setLandError('');
        try {
            let scheduleId = '';
            if (landIsRecurring) {
                if (landRecurringChoice === '__new__') {
                    const txn = (rawTransactions || []).find(t => t.id === landTxnId) || {};
                    const txAmt = (()=>{ try { return Math.abs(Number(typeof txn.amount === 'object' && txn.amount?.amount != null ? txn.amount.amount : txn.amount)); } catch(e) { return 0; } })();
                    const txDate = (txn.transaction_date || txn.post_date || new Date().toISOString().slice(0,10));
                    const payload = {
                        asset: Number(landChild.id),
                        description: (landRecurringName && landRecurringName.trim().length ? landRecurringName : (txn.description || 'Recurring landscaping expense')),
                        recurring: true,
                        recurring_frequency: landFrequency,
                        expected_maintenance_date: txDate,
                        expected_expense_amount: Number(txAmt) || 0
                    };
                    const cr = await api.post('/balance_sheet/assets/physical_assets/add-maintenance-item/', payload);
                    scheduleId = cr?.data?.id;
                } else if (landRecurringChoice) {
                    scheduleId = landRecurringChoice;
                }
            }
            await api.post(`/balance_sheet/financing/revolvers/transactions/${landTxnId}/link-asset-expense/`, {
                asset_id: Number(landChild.id),
                expense_category: 'landscaping',
                schedule_id: scheduleId || undefined,
                // Keep recurring fields for metadata
                recurring: landIsRecurring,
                recurring_frequency: landIsRecurring ? landFrequency : null,
                recurring_name: (landRecurringChoice === '__new__') ? (landRecurringName || undefined) : undefined,
            });
            setShowLandModal(false);
            setLandTxnId(null);
            await fetchTransactions();
        } catch (e) {
            setLandError('Failed to link expense.');
        } finally {
            setLandLoading(false);
        }
    };

    // Waste Management modal state
        const [showWasteModal, setShowWasteModal] = useState(false);
        const [wasteTxnId, setWasteTxnId] = useState(null);
        const [wasteLoading, setWasteLoading] = useState(false);
        const [wasteError, setWasteError] = useState('');
        const [wasteProperties, setWasteProperties] = useState([]);
        const [wasteSelectedPropertyId, setWasteSelectedPropertyId] = useState('');
        const [wasteAssetOptions, setWasteAssetOptions] = useState([]); // structures only
        const [wasteSelectedAssetId, setWasteSelectedAssetId] = useState('');
        const [wasteIsRecurring, setWasteIsRecurring] = useState(false);
        const [wasteFrequency, setWasteFrequency] = useState('monthly');
        const [wasteSeriesOptions, setWasteSeriesOptions] = useState([]);
        const [wasteRecurringMode, setWasteRecurringMode] = useState('existing'); // 'existing' | 'new'
        const [wasteExistingSeriesName, setWasteExistingSeriesName] = useState('');
        const [wasteRecurringName, setWasteRecurringName] = useState('');
        const [wasteRecurringType, setWasteRecurringType] = useState('variable');

        const openWasteModal = async (transactionId) => {
            setWasteTxnId(transactionId);
            setShowWasteModal(true);
            setWasteLoading(true);
            setWasteError('');
            setWasteSelectedPropertyId('');
            setWasteAssetOptions([]);
            setWasteSelectedAssetId('');
            setWasteSeriesOptions([]);
            setWasteExistingSeriesName('');
            setWasteRecurringName('');
            setWasteRecurringMode('existing');
            try {
                const res = await api.get('/balance_sheet/assets/physical_assets/property-choices/');
                setWasteProperties(Array.isArray(res.data) ? res.data : []);
            } catch (e) {
                setWasteProperties([]);
                setWasteError('Failed to load properties.');
            } finally {
                setWasteLoading(false);
            }
        };

        const onChangePropertyForWaste = async (pid) => {
            setWasteSelectedPropertyId(pid);
            setWasteAssetOptions([]);
            setWasteSelectedAssetId('');
            setWasteSeriesOptions([]);
            setWasteExistingSeriesName('');
            setWasteRecurringName('');
            setWasteRecurringMode('existing');
            if (!pid) return;
            setWasteLoading(true);
            setWasteError('');
            try {
                const opts = await api.get(`/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
                const children = (opts.data && Array.isArray(opts.data.children)) ? opts.data.children : [];
                let structs = children.filter(ch => (String(ch.type || '').toLowerCase()) !== 'land');
                let options = structs.map(ch => ({ id: ch.id, label: ch.label || `Structure ${ch.id}` }));
                if (!options.length) {
                    try {
                        const propRes = await api.get(`/balance_sheet/assets/physical_assets/resi_properties/${pid}/`);
                        const structsArr = Array.isArray(propRes.data?.structures) ? propRes.data.structures : [];
                        options = structsArr.map((s, idx) => ({ id: s.id, label: `Structure ${idx + 1}` }));
                    } catch (e) { /* ignore */ }
                }
                setWasteAssetOptions(options);
                if (!options.length) {
                    setWasteError('No structures found for the selected property. Please add a structure first.');
                }
            } catch (e) {
                setWasteError('Failed to load property details.');
                setWasteAssetOptions([]);
            } finally {
                setWasteLoading(false);
            }
        };

        useEffect(() => {
            const run = async () => {
                try {
                    if (!wasteSelectedPropertyId) { setWasteSeriesOptions([]); setWasteExistingSeriesName(''); return; }
                    const res = await api.get(`/balance_sheet/assets/physical_assets/${wasteSelectedPropertyId}/recurring-series/`, { params: { include_children: 1, include_inactive: 1 } });
                    const items = Array.isArray(res.data) ? res.data.map(s => ({ id: String(s.id), name: s.name, type: s.type })) : [];
                    setWasteSeriesOptions(items);
                } catch (e) { setWasteSeriesOptions([]); }
            };
            run();
        }, [wasteSelectedPropertyId]);

        const submitWasteLink = async () => {
            if (!wasteTxnId) return;
            if (!wasteSelectedPropertyId) { setWasteError('Please select a property.'); return; }
            if (!wasteSelectedAssetId) { setWasteError('Please select a structure.'); return; }
            setWasteLoading(true);
            setWasteError('');
            try {
                if (wasteIsRecurring) {
                    if (wasteRecurringMode === 'existing') {
                        if (!wasteExistingSeriesName) { setWasteLoading(false); setWasteError('Please select an existing recurring series.'); return; }
                    } else {
                        const name = (wasteRecurringName || '').trim();
                        if (!name) { setWasteLoading(false); setWasteError('Please enter a name for the new recurring series.'); return; }
                        const dup = (wasteSeriesOptions || []).some(s => String(s.name).toLowerCase() === name.toLowerCase());
                        if (dup) { setWasteLoading(false); setWasteError('A recurring series with this name already exists for this structure.'); return; }
                    }
                }
                // Resolve recurringSeriesId
                let recurringSeriesId = null;
                if (wasteIsRecurring) {
                    if (wasteRecurringMode === 'existing') {
                        recurringSeriesId = Number(wasteExistingSeriesName);
                    } else {
                        const body = { action: 'create', name: (wasteRecurringName || 'Recurring expense'), recurring_type: (wasteRecurringType || 'variable'), recurring_frequency: wasteFrequency, include_children: 1 };
                        const cr = await api.post(`/balance_sheet/assets/physical_assets/${wasteSelectedPropertyId}/recurring-series/manage/`, body);
                        if (cr?.data?.id != null) recurringSeriesId = Number(cr.data.id);
                    }
                }
                await api.post(`/balance_sheet/financing/revolvers/transactions/${wasteTxnId}/link-asset-expense/`, {
                    asset_id: Number(wasteSelectedAssetId),
                    expense_category: 'maintenance',
                    recurring: wasteIsRecurring,
                    recurring_frequency: wasteIsRecurring && wasteRecurringMode === 'new' ? wasteFrequency : null,
                    recurring_name: wasteIsRecurring && wasteRecurringMode === 'new' ? (wasteRecurringName || undefined) : undefined,
                    recurring_type: wasteIsRecurring && wasteRecurringMode === 'new' ? (wasteRecurringType || 'variable') : null,
                    recurring_series_id: recurringSeriesId != null ? recurringSeriesId : undefined,
                });
                setShowWasteModal(false);
                setWasteTxnId(null);
                await fetchTransactions();
            } catch (e) {
                setWasteError('Failed to link expense.');
            } finally {
                setWasteLoading(false);
            }
        };

        // Home (Property/Structure) modal state and handlers
    const [showHomeModal, setShowHomeModal] = useState(false);
    const [homeTxnId, setHomeTxnId] = useState(null);
    const [homeUiCategory, setHomeUiCategory] = useState('');
    const [homeLoading, setHomeLoading] = useState(false);
    const [homeError, setHomeError] = useState('');
    const [homeProperties, setHomeProperties] = useState([]);
    const [homeSelectedPropertyId, setHomeSelectedPropertyId] = useState('');
    const [homeAssetOptions, setHomeAssetOptions] = useState([]); // structures or property fallback
    const [homeSelectedAssetId, setHomeSelectedAssetId] = useState('');
    const [homeIsRecurring, setHomeIsRecurring] = useState(false);
    const [homeFrequency, setHomeFrequency] = useState('monthly');
    const [homeSeriesOptions, setHomeSeriesOptions] = useState([]); // [{id,name,frequency}]
    const [homeRecurringMode, setHomeRecurringMode] = useState('existing'); // 'existing' | 'new'
    const [homeRecurringChoice, setHomeRecurringChoice] = useState(''); // existing id or '__new__'
    const [homeRecurringName, setHomeRecurringName] = useState('');
    const [homeRecurringType, setHomeRecurringType] = useState('variable');
    const [homeRecurringLoading, setHomeRecurringLoading] = useState(false);

    const HOME_UI_TO_ASSET = {
    home_repair: 'repair',
    home_maintenance: 'maintenance',
    home_enhancement: 'enhancement',
    home_decor: 'home_decor',
    home_furniture: 'home_furniture',
    home_artwork: 'home_artwork',
    home_pest_control: 'pest_control',
    home_inspection: 'home_inspection',
    home_tax_payment: 'taxes',
    home_insurance_payment: 'insurance',
    home_other: 'home_other',
  };

    const openHomeModal = async (transactionId, uiCategory) => {
        setHomeTxnId(transactionId);
        setHomeUiCategory(uiCategory);
        setShowHomeModal(true);
        setHomeLoading(true);
        setHomeError('');
        setHomeSelectedPropertyId('');
        setHomeAssetOptions([]);
        setHomeSelectedAssetId('');
        try {
            const res = await api.get('/balance_sheet/assets/physical_assets/property-choices/');
            setHomeProperties(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setHomeProperties([]);
            setHomeError('Failed to load properties.');
        } finally {
            setHomeLoading(false);
        }
    };

    const onChangePropertyForHome = async (pid) => {
        setHomeSelectedPropertyId(pid);
        setHomeAssetOptions([]);
        setHomeSelectedAssetId('');
        // reset recurring selectors on property change
        setHomeRecurringChoice('');
        setHomeSeriesOptions([]);
        if (!pid) return;
        setHomeLoading(true);
        setHomeError('');
        try {
            // Use enriched linkage-options to include property itself, structures, and land
            const resp = await api.get(`/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
            const root = resp?.data || {};
            const children = Array.isArray(root.children) ? root.children : [];
            const options = [];
            // Add the entire property as a selectable option if available
            if (root.id) {
                const plabel = root.label || (root.address ? `Residential Property - ${root.address}` : `Residential Property - ${pid}`);
                options.push({ id: root.id, label: plabel });
            }
            // Add all children (structures and land)
            for (const ch of children) {
                if (!ch || !ch.id) continue;
                const lbl = ch.label || (ch.type ? `${String(ch.type).charAt(0).toUpperCase() + String(ch.type).slice(1)} ${ch.id}` : `Asset ${ch.id}`);
                options.push({ id: ch.id, label: lbl });
            }
            setHomeAssetOptions(options);
            if (!options.length) {
                setHomeError('No linkable assets found for the selected property. Please add a Structure or Land first.');
            }
        } catch (e) {
            setHomeError('Failed to load property details.');
            setHomeAssetOptions([]);
        } finally {
            setHomeLoading(false);
        }
    };

    const submitHomeLink = async () => {
        if (!homeTxnId || !homeSelectedAssetId || !homeUiCategory) {
            setHomeError('Please select a property and asset.');
            return;
        }
        setHomeLoading(true);
        setHomeError('');
        try {
            const assetCat = HOME_UI_TO_ASSET[homeUiCategory] || 'maintenance';
            let scheduleId = '';
            if (homeIsRecurring) {
                if (homeRecurringChoice === '__new__') {
                    const txn = (rawTransactions || []).find(t => t.id === homeTxnId) || {};
                    const txAmt = (()=>{ try { return Math.abs(Number(txn.amount)); } catch(e) { return 0; } })();
                    const txDate = (txn.transaction_date || txn.post_date || new Date().toISOString().slice(0,10));
                    const payload = {
                        asset: Number(homeSelectedAssetId),
                        description: (homeRecurringName && homeRecurringName.trim().length ? homeRecurringName : (txn.description || 'Recurring home expense')),
                        recurring: true,
                        recurring_frequency: homeFrequency,
                        expected_maintenance_date: txDate,
                        expected_expense_amount: Number(txAmt) || 0
                    };
                    const cr = await api.post('/balance_sheet/assets/physical_assets/add-maintenance-item/', payload);
                    scheduleId = cr?.data?.id;
                } else if (homeRecurringChoice) {
                    scheduleId = homeRecurringChoice;
                }
            }
            await api.post(`/balance_sheet/financing/revolvers/transactions/${homeTxnId}/link-asset-expense/`, {
                asset_id: Number(homeSelectedAssetId),
                expense_category: assetCat,
                schedule_id: scheduleId || undefined,
                recurring: homeIsRecurring,
                recurring_frequency: homeIsRecurring ? homeFrequency : null,
                recurring_name: (homeRecurringChoice === '__new__') ? (homeRecurringName || undefined) : undefined,
                recurring_type: homeIsRecurring ? (homeRecurringType || null) : null,
            });
            setShowHomeModal(false);
            setHomeTxnId(null);
            await fetchTransactions();
        } catch (e) {
            setHomeError('Failed to link home expense.');
        } finally {
            setHomeLoading(false);
        }
    };

    // Vehicle linking modal state (revolver)
    const [showAutoModal, setShowAutoModal] = useState(false);
    const [autoTxnId, setAutoTxnId] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [assetExpenseChoices, setAssetExpenseChoices] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [autoForm, setAutoForm] = useState({
        vehicleId: '', assetExpenseCategory: 'maintenance', isRecurring: false,
        scheduleMode: 'existing', scheduleId: '',
        newSchedule: { description: '', frequency: 'monthly', expected_date: '', expected_amount: '' },
        loading: false, error: ''
    });

    const openAutoModal = async (transactionId) => {
        setAutoTxnId(transactionId);
        const txn = (rawTransactions || []).find(t => t.id === transactionId) || null;
        const amt = txn ? Math.abs(Number(typeof txn.amount === 'object' && txn.amount.amount != null ? txn.amount.amount : txn.amount)) : '';
        const dt = txn ? (txn.transaction_date || txn.post_date) : new Date().toISOString().slice(0,10);
        setAutoForm(prev => ({ ...prev, vehicleId:'', assetExpenseCategory:'maintenance', isRecurring:false, scheduleMode:'existing', scheduleId:'',
            newSchedule: { description: txn?.description || '', frequency:'monthly', expected_date: dt, expected_amount: amt }, loading:true, error:'' }));
        setSchedules([]);
        setShowAutoModal(true);
        try {
            const phys = await api.get('/balance_sheet/assets/physical_assets/');
            setVehicles((Array.isArray(phys.data) ? phys.data : []).filter(a => (a.asset_type_2 || '').toLowerCase()==='vehicle'));
            const aec = await api.get('/balance_sheet/assets/asset-expense-category-choices/');
            setAssetExpenseChoices(Array.isArray(aec.data) ? aec.data.map(([v,l])=>({value:v,label:l})) : []);
        } catch (e) { setAutoForm(prev=> ({ ...prev, error:'Failed to load vehicles or categories.'})); }
        finally { setAutoForm(prev=> ({ ...prev, loading:false })); }
    };

    const onChangeVehicle = async (vid) => {
        setAutoForm(prev=> ({ ...prev, vehicleId: vid, loading:true, error:'', scheduleId:'' }));
        setSchedules([]);
        if (!vid) { setAutoForm(prev=> ({ ...prev, loading:false })); return; }
        try { const res = await api.get(`/balance_sheet/assets/physical_assets/${vid}/maintenance-items/`); setSchedules(Array.isArray(res.data)? res.data: []); }
        catch (e) { setSchedules([]); }
        finally { setAutoForm(prev=> ({ ...prev, loading:false })); }
    };

    const submitAutoLink = async () => {
        if (!autoTxnId || !autoForm.vehicleId) { setAutoForm(prev=> ({ ...prev, error:'Please select a vehicle.' })); return; }
        setAutoForm(prev=> ({ ...prev, loading:true, error:'' }));
        let schedId = '';
        try {
            if (autoForm.isRecurring && autoForm.scheduleMode==='new') {
                const payload = { asset:Number(autoForm.vehicleId), description:autoForm.newSchedule.description || (rawTransactions.find(t=>t.id===autoTxnId)?.description)||'Recurring vehicle expense', recurring:true, recurring_frequency:autoForm.newSchedule.frequency, expected_maintenance_date:autoForm.newSchedule.expected_date, expected_expense_amount:Number(autoForm.newSchedule.expected_amount)||0 };
                const cr = await api.post('/balance_sheet/assets/physical_assets/add-maintenance-item/', payload); schedId = cr?.data?.id;
            } else if (autoForm.isRecurring && autoForm.scheduleMode==='existing') {
                schedId = autoForm.scheduleId || '';
            }
            await api.post(`/balance_sheet/financing/revolvers/transactions/${autoTxnId}/link-asset-expense/`, {
                asset_id: Number(autoForm.vehicleId), expense_category: autoForm.assetExpenseCategory || 'maintenance', schedule_id: schedId || undefined,
                description: (rawTransactions.find(t=>t.id===autoTxnId)?.description) || undefined,
            });
            setShowAutoModal(false); setAutoTxnId(null);
            await fetchTransactions();
        } catch (e) { setAutoForm(prev=> ({ ...prev, error:'Failed to link vehicle expense.' })); }
        finally { setAutoForm(prev=> ({ ...prev, loading:false })); }
    };

    const submitAddLand = async () => {
        if (!selectedPropertyId) { setLandFormError('Please select a property first.'); return; }
        setLandFormError('');
        setLandFormLoading(true);
        try {
            const payload = {
                asset_type_2: 'land',
                residential_property: Number(selectedPropertyId),
                legal_description: landForm.legal_description,
                parcel_number: landForm.parcel_number,
                lot_number: landForm.lot_number ? Number(landForm.lot_number) : null,
                plat_number: landForm.plat_number ? Number(landForm.plat_number) : null,
                tax_district: landForm.tax_district,
                area_in_acres: landForm.area_in_acres ? Number(landForm.area_in_acres) : null,
                latitude: landForm.latitude || null,
                longitude: landForm.longitude || null,
                survey_description: landForm.survey_description || '',
                financing_allocation: landForm.financing_allocation ? Number(landForm.financing_allocation) : null
            };
            const res = await api.post('/balance_sheet/assets/physical_assets/resi_properties/land/create-land-asset/', payload);
            const created = res.data;
            const label = created && created.area_in_acres ? `Land — ${created.area_in_acres} acres` : 'Land';
            setLandChild({ id: created.id, label });
            setShowAddLand(false);
        } catch (e) {
            setLandFormError('Failed to create Land. Please check inputs.');
        } finally {
            setLandFormLoading(false);
        }
    };

    const updateExpenseCategory = async (transactionId, category) => {
        // Handle synthetic IDs (e.g., synth-123-456) for split child transactions
        let realId = transactionId;
        if (typeof transactionId === 'string' && transactionId.startsWith('synth-')) {
            const parts = transactionId.split('-');
            if (parts.length >= 3) {
                realId = parts[parts.length - 1];
                console.log(`Resolved synthetic ID ${transactionId} to real ID ${realId}`);
            }
        }

        try {
            await api.patch(`/balance_sheet/financing/revolvers/transactions/${realId}/update_expense_category/`, {
                expense_category: category
            });

            // Update local state - update rawTransactions since that's our source
            setRawTransactions(prev => prev.map(transaction => {
                if (transaction.id === transactionId) {
                    return { ...transaction, expense_category: category };
                }
                if (transaction.children && transaction.children.length > 0) {
                    return {
                        ...transaction,
                        children: transaction.children.map(child =>
                            child.id === transactionId || String(child.id) === String(realId)
                                ? { ...child, expense_category: category }
                                : child
                        )
                    };
                }
                return transaction;
            }));

            // Close dropdown after selection
            setOpenDropdownId(null);

            // If income selected (including deprecated categories), open the new income link modal
            if (category === 'income' || category === 'income_nonrecurring' || category === 'income_recurring') {
                try {
                    const txn = rawTransactions.find(t => t.id === transactionId);
                    if (txn) {
                        setIncomeLinkRow({ id: txn.id, description: txn.description, amount: txn.amount, date: txn.transaction_date });
                        setShowIncomeLinkModal(true);
                    }
                } catch (e) { /* ignore */ }
            }

            // If Automotive or Gas & Fuel, prompt vehicle linking modal
            if (category === 'automotive' || category === 'gas_fuel') {
                const txn = rawTransactions.find(t => t.id === transactionId);
                if (txn) {
                    setUtilityRow({
                        id: txn.id,
                        sourceType: 'revolver',
                        date: txn.transaction_date,
                        description: txn.description,
                        amount: txn.amount,
                        linked_asset_id: txn.linked_asset_id || '',
                        linked_asset_expense_id: txn.linked_asset_expense_id || '',
                        linked_recurring_series_id: txn.linked_recurring_series_id || '',
                        expense_category: category,
                    });
                    setShowUtilityModal(true);
                }
            }
            // If Landscaping selected, prompt linking modal
            if (category === 'landscaping') {
                const txn = rawTransactions.find(t => t.id === transactionId);
                if (txn) {
                    setUtilityRow({
                        id: txn.id,
                        sourceType: 'revolver',
                        date: txn.transaction_date,
                        description: txn.description,
                        amount: txn.amount,
                        linked_asset_id: txn.linked_asset_id || '',
                        linked_asset_expense_id: txn.linked_asset_expense_id || '',
                        linked_recurring_series_id: txn.linked_recurring_series_id || '',
                        expense_category: category,
                    });
                    setShowUtilityModal(true);
                }
            }
            // If Home category child selected, open Property/Structure modal
            if ([
                'home_repair','home_maintenance','home_enhancement','home_decor','home_furniture','home_artwork','home_pest_control',
                'home_inspection','home_insurance_payment', 'home_plants'
            ].includes(category)) {
                const txn = rawTransactions.find(t => t.id === transactionId);
                if (txn) {
                    setUtilityRow({
                        id: txn.id,
                        sourceType: 'revolver',
                        date: txn.transaction_date,
                        description: txn.description,
                        amount: txn.amount,
                        linked_asset_id: txn.linked_asset_id || '',
                        linked_asset_expense_id: txn.linked_asset_expense_id || '',
                        linked_recurring_series_id: txn.linked_recurring_series_id || '',
                        expense_category: category,
                    });
                    setShowUtilityModal(true);
                }
            }
            // If any Utilities child or Taxes -> open linkage modal
            const isUtilitiesOrTaxes = (slug) => {
                if (String(slug) === 'taxes' || String(slug) === 'home_tax_payment') return true;
                try {
                    const resp = expenseCategories || { items: [] };
                    for (const it of (resp.items || [])) {
                        if (String(it.value) === 'utilities' && Array.isArray(it.children)) {
                            return it.children.some(ch => String(ch.value) === String(slug));
                        }
                    }
                } catch (e) {}
                const KNOWN = new Set(['water','gas','electricity','electric_gas','utilities__other']);
                return KNOWN.has(String(slug));
            };
            if (isUtilitiesOrTaxes(category)) {
                const txn = rawTransactions.find(t => t.id === transactionId);
                if (txn) {
                    setUtilityRow({
                        id: txn.id,
                        sourceType: 'revolver',
                        date: txn.transaction_date,
                        description: txn.description,
                        amount: txn.amount,
                        linked_asset_id: txn.linked_asset_id || '',
                        linked_asset_expense_id: txn.linked_asset_expense_id || '',
                        linked_utility_bill_id: txn.linked_utility_bill_id || '',
                        linked_recurring_series_id: txn.linked_recurring_series_id || '',
                        linked_property_id: txn.linked_property_id || '',
                        expense_category: category,
                    });
                    setShowUtilityModal(true);
                }
            }
            // If Debt Disbursement selected, open debt disbursement modal
            if (category === 'debt_disbursement') {
                const txn = rawTransactions.find(t => t.id === transactionId);
                if (txn) {
                    setDebtDisbursementRow(txn);
                    setShowDebtDisbursementModal(true);
                }
            }
            // If any Waste Management child -> open Waste link modal (property + structure)
            const isWasteChild = (slug) => {
                try {
                    const resp = expenseCategories || { items: [] };
                    for (const it of (resp.items || [])) {
                        if (String(it.value) === 'waste_management' && Array.isArray(it.children)) {
                            return it.children.some(ch => String(ch.value) === String(slug));
                        }
                    }
                } catch (e) {}
                const KNOWN = new Set(['sanitation','waste_management__recycling','waste_management__yard_waste','waste_management__other']);
                return KNOWN.has(String(slug));
            };
            if (isWasteChild(category)) {
                const txn = rawTransactions.find(t => t.id === transactionId);
                if (txn) {
                    setUtilityRow({
                        id: txn.id,
                        sourceType: 'revolver',
                        date: txn.transaction_date,
                        description: txn.description,
                        amount: txn.amount,
                        linked_asset_id: txn.linked_asset_id || '',
                        linked_asset_expense_id: txn.linked_asset_expense_id || '',
                        linked_recurring_series_id: txn.linked_recurring_series_id || '',
                        expense_category: category,
                    });
                    setShowUtilityModal(true);
                }
            }
        } catch (error) {
            console.error('Error updating expense category:', error);
        }
    };

    // Toggle dropdown
    const toggleDropdown = useCallback((e, transactionId) => {
        if (e) e.stopPropagation();
        setOpenDropdownId(prev => prev === transactionId ? null : transactionId);
    }, []);

    // Payment linking helpers
    const closePaymentModal = () => {
        setShowPaymentModal(false);
        setActivePaymentTxnId(null);
        setSelectedDebt({ type: '', id: '' });
        setPaymentCandidates([]);
        setSelectedCandidateId('');
    };

    const onDebtChange = async (e) => {
        const value = e.target.value || '';
        if (!value) {
            setSelectedDebt({ type: '', id: '' });
            setPaymentCandidates([]);
            setSelectedCandidateId('');
            return;
        }
        try {
            const [type, idStr] = value.split('|');
            const idNum = parseInt(idStr);
            setSelectedDebt({ type, id: String(idNum) });
            if (!activePaymentTxnId) return;
            setLoadingPayment(true);
            const res = await api.get(`${apiBase}/transactions/${activePaymentTxnId}/candidate-debt-payments/`, {
                params: { debt_type: type, debt_id: idNum }
            });
            setPaymentCandidates(res.data.candidates || []);
            setSelectedCandidateId('');
        } catch (err) {
            setPaymentCandidates([]);
            setSelectedCandidateId('');
        } finally {
            setLoadingPayment(false);
        }
    };

    const linkSelectedPayment = async () => {
        if (!activePaymentTxnId) return;
        if (!selectedDebt.type || !selectedDebt.id) {
            alert('Please select a debt to link to.');
            return;
        }
        if (!selectedCandidateId) {
            alert('Please select a matching payment candidate.');
            return;
        }
        try {
            setLoadingPayment(true);
            await api.post(`${apiBase}/transactions/${activePaymentTxnId}/link-debt-payment/`, {
                debt_type: selectedDebt.type,
                debt_id: parseInt(selectedDebt.id),
                candidate_id: parseInt(selectedCandidateId)
            });
            // Refresh transactions and close
            await fetchTransactions();
            closePaymentModal();
            window.alert('Payment linked successfully.');
        } catch (e) {
            alert('Failed to link payment');
        } finally {
            setLoadingPayment(false);
        }
    };

    // Add new transaction functions
    const openAddModal = () => {
        // Only allow adding transactions for individual cards, not "all" view
        if (isAllCardsView || id === 'all') {
            alert('Please select a specific credit card to add transactions.');
            return;
        }

        // Reset form with default values
        setNewTransaction({
            transaction_date: new Date().toISOString().split('T')[0], // Today's date
            post_date: new Date().toISOString().split('T')[0], // Today's date
            description: '',
            amount: '',
            expense_category: 'none'
        });
        setShowAddModal(true);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setNewTransaction({
            transaction_date: '',
            post_date: '',
            description: '',
            amount: '',
            expense_category: 'none'
        });
    };

    const handleAddTransaction = async () => {
        // Validation
        if (!newTransaction.transaction_date.trim()) {
            alert('Transaction date is required');
            return;
        }
        if (!newTransaction.post_date.trim()) {
            alert('Post date is required');
            return;
        }
        if (!newTransaction.description.trim()) {
            alert('Description is required');
            return;
        }
        if (!newTransaction.amount || isNaN(parseFloat(newTransaction.amount))) {
            alert('Please enter a valid amount');
            return;
        }

        setAddingTransaction(true);

        try {
            // Create the transaction via API
            const transactionData = {
                revolver: parseInt(id), // Convert to integer
                transaction_date: newTransaction.transaction_date,
                post_date: newTransaction.post_date,
                description: newTransaction.description.trim(),
                amount: parseFloat(newTransaction.amount),
                expense_category: newTransaction.expense_category,
                source: 'user_supplied'
            };

            const response = await api.post(
                `/balance_sheet/financing/revolvers/transactions/`,
                transactionData
            );

            console.log('Transaction created successfully:', response.data);

            // Add the new transaction to local state immediately to prevent duplicates
            const newTransactionFromAPI = response.data;
            setRawTransactions(prev => {
                // Check if transaction already exists to prevent duplicates
                const exists = prev.some(t => t.id === newTransactionFromAPI.id);
                if (exists) {
                    return prev; // Don't add if it already exists
                }
                return [newTransactionFromAPI, ...prev]; // Add to beginning
            });

            // Try auto-assign on the newly added transaction using current list + new one
            try { await autoAssignCategories([newTransactionFromAPI, ...rawTransactions]); } catch(e) { /* ignore */ }

            // Close modal
            closeAddModal();

            // Refresh statement summary data (for charts) but don't refetch transactions
            await fetchStatementSummaryData(statements.length > 1);

            // Show success message
            alert('Transaction added successfully!');

        } catch (error) {
            console.error('Error adding transaction:', error);

            // More detailed error logging
            if (error.response) {
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);

                // Show more specific error message
                let errorMessage = 'Failed to add transaction. ';
                if (error.response.data && typeof error.response.data === 'object') {
                    errorMessage += Object.entries(error.response.data)
                        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                        .join('; ');
                } else {
                    errorMessage += error.response.data || 'Please try again.';
                }
                alert(errorMessage);
            } else {
                alert('Network error. Please try again.');
            }
        } finally {
            setAddingTransaction(false);
        }
    };

    // Helper function to format currency
    const formatCurrency = (amount, decimalPlaces = 2) => {
        const roundedAmount = Number(amount).toFixed(decimalPlaces);
        return `$${parseFloat(roundedAmount).toLocaleString(undefined, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
    };

    // Helper function to format date without timezone shifts
    // Preserves YYYY-MM-DD values exactly (renders as Mon DD, YYYY) and only falls back to Date parsing when necessary
    const formatDate = (dateString) => {
        if (!dateString) return '';
        // Handle plain date (YYYY-MM-DD) safely without creating a Date object (avoids TZ off-by-one)
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
        if (m) {
            const [, y, mo, d] = m;
            const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const mi = Math.max(1, Math.min(12, parseInt(mo, 10))) - 1;
            const di = parseInt(d, 10);
            return `${monthNames[mi]} ${di}, ${y}`;
        }
        // If it starts with YYYY-MM-DD but includes time, take the date part
        const m2 = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
        if (m2) {
            const [, y, mo, d] = m2;
            const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const mi = Math.max(1, Math.min(12, parseInt(mo, 10))) - 1;
            const di = parseInt(d, 10);
            return `${monthNames[mi]} ${di}, ${y}`;
        }
        // Fallback for other date formats
        const dt = new Date(dateString);
        if (isNaN(dt.getTime())) return String(dateString);
        return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Get category styling
    const getCategoryStyle = (category) => {
        const styles = {
            'food_dining': { backgroundColor: '#FFF3E0', color: '#E65100', borderColor: '#E65100' },
            'gas_fuel': { backgroundColor: '#F3E5F5', color: '#7B1FA2', borderColor: '#7B1FA2' },
            'groceries': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
            'internet': { backgroundColor: '#E3F2FD', color: '#1565C0', borderColor: '#1565C0' },
            'apps': { backgroundColor: '#E8EAF6', color: '#5E35B1', borderColor: '#5E35B1' },
            'books': { backgroundColor: '#FEF3C7', color: '#78350F', borderColor: '#FBBF24' },
            'landscaping': { backgroundColor: '#E0F2E1', color: '#388E3C', borderColor: '#388E3C' },
            'shopping': { backgroundColor: '#FFE0E6', color: '#C2185B', borderColor: '#C2185B' },
            'entertainment': { backgroundColor: '#E1F5FE', color: '#0277BD', borderColor: '#0277BD' },
            'travel': { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
            'automotive': { backgroundColor: '#E0F2F1', color: '#00695C', borderColor: '#00695C' },
            'healthcare': { backgroundColor: '#FFEBEE', color: '#C62828', borderColor: '#C62828' },
            'utilities': { backgroundColor: '#FFF3E0', color: '#EF6C00', borderColor: '#EF6C00' },
            'subscription': { backgroundColor: '#F3E5F5', color: '#8E24AA', borderColor: '#8E24AA' },
            'insurance': { backgroundColor: '#E8EAF6', color: '#3F51B5', borderColor: '#3F51B5' },
            'taxes': { backgroundColor: '#FFEBEE', color: '#B71C1C', borderColor: '#B71C1C' },
            'kids__other': { backgroundColor: '#E8F5E8', color: '#43A047', borderColor: '#43A047' },
            'education': { backgroundColor: '#E0F7FA', color: '#0097A7', borderColor: '#0097A7' },
            'business': { backgroundColor: '#ECEFF1', color: '#455A64', borderColor: '#455A64' },
            'personal_care': { backgroundColor: '#FCE4EC', color: '#AD1457', borderColor: '#AD1457' },
            'dental_products': { backgroundColor: '#FCE4EC', color: '#AD1457', borderColor: '#AD1457' },
            'workout_gym_products': { backgroundColor: '#F7FEE7', color: '#4D7C0F', borderColor: '#4D7C0F' },
            'home_improvement': { backgroundColor: '#F1F8E9', color: '#558B2F', borderColor: '#558B2F' },
            'gifts_charity': { backgroundColor: '#FFF9C4', color: '#9E9D24', borderColor: '#9E9D24' },
            'fees_charges': { backgroundColor: '#FFEBEE', color: '#D32F2F', borderColor: '#D32F2F' },
            'transfer': { backgroundColor: '#E3F2FD', color: '#1976D2', borderColor: '#1976D2' },
            'payments': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
            'rewards_cashback': { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
            'other': { backgroundColor: '#F5F5F5', color: '#616161', borderColor: '#616161' },
            'none': { backgroundColor: '#FFFFFF', color: '#000000', borderColor: '#000000' },
        };
        return styles[category] || styles['none'];
    };

    // Render expense category badge
    const renderExpenseCategory = (transaction) => {
            const dropdownId = transaction.id;
            const getLabelForValue = (val) => {
                const label = categoryLabelMap.get(String(val));
                return label || 'None';
            };
            const isParentExpanded = (parentVal) => {
                const set = expandedParents[dropdownId];
                return !!(set && set.has(parentVal));
            };
            const toggleParent = (parentVal) => {
                setExpandedParents(prev => {
                    const cur = new Set(prev[dropdownId] || []);
                    if (cur.has(parentVal)) cur.delete(parentVal); else cur.add(parentVal);
                    return { ...prev, [dropdownId]: cur };
                });
            };
        const category = transaction.expense_category || 'none';
        const label = getLabelForValue(category);
        const style = getCategoryStyle(category);
        const isOpen = openDropdownId === transaction.id;

        return (
            <div className="position-relative" style={{ display: 'inline-block' }}>
                <button
                    className="btn btn-sm"
                    type="button"
                    onClick={(e) => toggleDropdown(e, transaction.id)}
                    style={{
                        backgroundColor: style.backgroundColor,
                        color: style.color,
                        borderColor: style.borderColor,
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        padding: '4px 8px',
                        border: `1px solid ${style.borderColor}`,
                        cursor: 'pointer',
                        whiteSpace:'nowrap'
                    }}
                >
                    {label} ▼
                </button>

                {isOpen && (
                    <div
                        className="position-absolute"
                        style={{
                            top: '100%',
                            left: '0',
                            zIndex: 1000,
                            minWidth: '320px',
                            backgroundColor: '#111827',
                            border: '1px solid #374151',
                            borderRadius: '0.375rem',
                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.3)',
                            marginTop: '2px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ maxHeight: '480px', overflowY: 'auto', padding: '4px 0' }}>
                            {expenseCategories.items.map((it) => (
                                <div key={it.value}>
                                    <button
                                        className="w-100 text-start"
                                        style={{
                                            width: '100%',
                                            display: 'block',
                                            padding: '8px 16px',
                                            border: 'none',
                                            backgroundColor: '#111827',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            color: '#e5e7eb'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                        onClick={() => {
                                            if (Array.isArray(it.children) && it.children.length > 0) {
                                                toggleParent(it.value);
                                            } else {
                                                updateExpenseCategory(transaction.id, String(it.value));
                                                setOpenDropdownId(null);
                                            }
                                        }}
                                    >
                                        {(Array.isArray(it.children) && it.children.length > 0) ? (isParentExpanded(it.value) ? '▼ ' : '► ') : ''}
                                        {it.label}
                                    </button>
                                    {(Array.isArray(it.children) && it.children.length > 0 && isParentExpanded(it.value)) && (
                                        <div>
                                            {it.children.map(ch => (
                                                <button
                                                    key={ch.value}
                                                    className="w-100 text-start"
                                                    style={{
                                                        display: 'block',
                                                        padding: '6px 16px 6px 28px',
                                                        border: 'none',
                                                        backgroundColor: '#111827',
                                                        cursor: 'pointer',
                                                        fontSize: '0.875rem',
                                                        color: '#e5e7eb'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                                    onClick={() => { updateExpenseCategory(transaction.id, String(ch.value)); setOpenDropdownId(null); }}
                                                >
                                                    {ch.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {/* None at bottom */}
                            <div style={{ borderTop: '1px solid #374151', margin: '4px 0' }} />
                            <button
                                className="w-100 text-start"
                                style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                onClick={() => { updateExpenseCategory(transaction.id, String(expenseCategories.noneItem.value)); setOpenDropdownId(null); }}
                            >
                                {expenseCategories.noneItem.label || 'None'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render editable cell
    const renderEditableCell = () => null;

    if (loading) return (
        <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-auto">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading credit card details...</p>
                </div>
            </div>
        </div>
    );

    if (error) return (
        <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-md-6 text-center">
                    <div className="alert alert-danger">
                        <h4>Error</h4>
                        <p>{error}</p>
                        <button className="btn btn-primary mt-2" onClick={() => window.location.reload()}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!creditCard) return (
        <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-auto text-center">
                    <div className="alert alert-warning">
                        <h4>No Credit Card Found</h4>
                        <p className="mb-0">The requested credit card could not be found.</p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="container py-4">
            {/* Credit Card Navigation Tabs - Enhanced with All button */}
            <div className="chart-card mb-3">
                <div className="mb-2">
                    <h6 className="card-title mb-2">Your Credit Cards</h6>
                    <ul className="nav nav-pills card-header-pills">
                        {/* All Cards Button */}
                        <li className="nav-item">
                            <button
                                className={`nav-link ${id === 'all' ? 'active' : ''}`}
                                onClick={() => handleCreditCardSwitch('all')}
                                disabled={switchingCard}
                                style={{
                                    cursor: switchingCard ? 'not-allowed' : 'pointer',
                                    opacity: switchingCard && id !== 'all' ? 0.6 : 1,
                                    fontWeight: 'bold',
                                    background: id === 'all' ? 'linear-gradient(135deg, #007bff, #0056b3)' : '',
                                    color: id === 'all' ? 'white' : '',
                                    border: id === 'all' ? '2px solid #0056b3' : '2px solid transparent',
                                    borderRadius: '8px',
                                    boxShadow: id === 'all' ? '0 4px 8px rgba(0,123,255,0.3)' : 'none',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {switchingCard && id !== 'all' ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                ) : null}
                                📊 All Cards
                            </button>
                        </li>

                        {/* Individual Card Buttons */}
                        {allCreditCards.map((card) => (
                            <li className="nav-item" key={card.id}>
                                <button
                                    className={`nav-link ${parseInt(id) === card.id ? 'active' : ''}`}
                                    onClick={() => handleCreditCardSwitch(card.id)}
                                    disabled={switchingCard}
                                    style={{
                                        cursor: switchingCard ? 'not-allowed' : 'pointer',
                                        opacity: switchingCard && parseInt(id) !== card.id ? 0.6 : 1,
                                        background: parseInt(id) === card.id ? 'linear-gradient(135deg, #28a745, #1e7e34)' : '',
                                        color: parseInt(id) === card.id ? 'white' : '',
                                        border: parseInt(id) === card.id ? '2px solid #1e7e34' : '2px solid transparent',
                                        borderRadius: '8px',
                                        boxShadow: parseInt(id) === card.id ? '0 4px 8px rgba(40,167,69,0.3)' : 'none',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    {switchingCard && parseInt(id) !== card.id ? (
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    ) : null}
                                    💳 {card.lender} *{card.last_four_digits_of_card}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    {/* Page Header */}
                    <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '12px' }}>
                        <div>
                            <h1 className="h2 mb-1">
                                {switchingCard ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Loading...
                                    </>
                                ) : (
                                    isAllCardsView || id === 'all'
                                        ? 'All Credit Cards Overview'
                                        : `Credit Card Details: ${creditCard.lender} *${creditCard.last_four_digits_of_card}`
                                )}
                            </h1>
                            <p className="text-muted mb-0">
                                {isAllCardsView || id === 'all'
                                    ? 'Comprehensive view of all your credit card transactions and statements'
                                    : 'Manage your credit card transactions and statements'
                                }
                            </p>
                        </div>
                        {/* Upload button next to title for individual cards */}
                        {!(isAllCardsView || id === 'all') && (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <PdfUpload
                                    apiEndpoint={apiEndpoint}
                                    lender={creditCard?.lender}
                                    object_id={creditCard?.id}
                                    existingStatements={statements}
                                    onSuccess={async () => {
                                        await fetchTransactions();
                                        await fetchStatementSummaryData(true);
                                    }}
                                />
                                <StatementSummaryForm
                                    revolver={creditCard}
                                    triggerLabel="Enter Statement Summary"
                                    triggerClassName="btn btn-secondary"
                                    onSuccess={async ()=>{
                                      try {
                                        // Refresh latest statement data and charts without full reload
                                        await fetchStatementSummaryData(false);
                                      } catch(e) {
                                        console.warn('Refresh after summary save failed', e);
                                      }
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {showLandModal && (
                        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
                            <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(680px, 90vw)' }}>
                                <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                                    <h5 style={{ margin:0 }}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===landTxnId); return (t && t.linked_asset_id) ? 'Edit Link — Landscaping Expense' : 'Link Landscaping Expense'; } catch(e){ return 'Link Landscaping Expense'; } })()}</h5>
                                    <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                                      {(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===landTxnId); return (t && t.linked_asset_id); } catch(e){ return false; } })() && (
                                        <button
                                          className="btn btn-sm btn-warning"
                                          title="Unlink from asset"
                                          onClick={async ()=>{
                                            try {
                                              await api.post(`/balance_sheet/financing/revolvers/transactions/${landTxnId}/unlink-asset-expense/`);
                                              setShowLandModal(false);
                                              setLandTxnId(null);
                                              await fetchTransactions();
                                            } catch(err){
                                              console.error('Failed to unlink landscaping expense', err);
                                              setLandError('Failed to unlink landscaping expense.');
                                            }
                                          }}
                                        >Unlink</button>
                                      )}
                                      <button className="btn btn-sm btn-secondary" onClick={()=> setShowLandModal(false)}>✕</button>
                                    </div>
                                </div>
                                <div style={{ padding:16 }}>
                                    {landError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{landError}</div>}
                                    <div className="mb-3">
                                        <label className="form-label">Residential Property</label>
                                        <select className="form-select" value={selectedPropertyId} onChange={(e)=> onChangePropertyForLand(e.target.value)} disabled={landLoading}>
                                            <option value="">Select property…</option>
                                            {properties.map(p => (<option key={p.id} value={p.id}>{p.address}</option>))}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Land</label>
                                        <div style={{ padding:8, border:'1px solid #374151', borderRadius:6, background:'#111827' }}>
                                            {selectedPropertyId ? (
                                                landLoading ? 'Loading land…' : (landChild ? (
                                                    landChild.label
                                                ) : (
                                                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                                                        <span>No Land configured for selected property.</span>
                                                        <button className="btn btn-sm btn-outline-info" onClick={()=> setShowAddLand(true)} disabled={landLoading}>Add Land</button>
                                                    </div>
                                                ))
                                            ) : (<span style={{ color:'#9ca3af' }}>Select a property to see its Land.</span>)}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-check">
                                            <input className="form-check-input" type="checkbox" checked={landIsRecurring} onChange={e=> setLandIsRecurring(e.target.checked)} />
                                            <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                                        </label>
                                        {landIsRecurring && (
                                            <div style={{ marginTop:8 }}>
                                                <label className="form-label">Frequency</label>
                                                <select className="form-select" value={landFrequency} onChange={e=> setLandFrequency(e.target.value)}>
                                                    <option value="weekly">Weekly</option>
                                                    <option value="monthly">Monthly</option>
                                                    <option value="bi_monthly">Bi-monthly</option>
                                                    <option value="quarterly">Quarterly</option>
                                                    <option value="semi_annually">Semi-annually</option>
                                                    <option value="annually">Annually</option>
                                                </select>
                                                <div style={{ marginTop:12 }}>
                                                    <label className="form-label">Recurring Series</label>
                                                    <select className="form-select" value={landRecurringChoice} onChange={(e)=> setLandRecurringChoice(e.target.value)}
                                                            disabled={!landChild || landRecurringLoading}
                                                            onFocus={async()=> { if (landChild && !landRecurringOptions.length) { await fetchLandRecurringOptions(landChild.id); } }}>
                                                        <option value="">{landRecurringLoading ? 'Loading…' : 'Select existing series…'}</option>
                                                        {landRecurringOptions.map(opt => (
                                                            <option key={opt.id} value={String(opt.id)}>
                                                                {opt.name} {opt.frequency ? `(${opt.frequency})` : ''}
                                                            </option>
                                                        ))}
                                                        <option value="__new__">Create new series…</option>
                                                    </select>
                                                    {landRecurringChoice === '__new__' && (
                                                        <div style={{ marginTop:8 }}>
                                                            <label className="form-label">New Series Name</label>
                                                            <input className="form-control" value={landRecurringName} onChange={e=> setLandRecurringName(e.target.value)} placeholder="e.g., Lawn Service" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex justify-content-end gap-2">
                                        <button className="btn btn-secondary" onClick={()=> setShowLandModal(false)} disabled={landLoading}>Cancel</button>
                                        <button className="btn btn-primary" onClick={submitLandLink} disabled={landLoading || !selectedPropertyId || !landChild}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===landTxnId); return (t && t.linked_asset_id) ? 'Save' : 'Link Expense'; } catch(e){ return 'Link Expense'; } })()}</button>
                                    </div>

                                    {showAddLand && (
                                        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
                                            <div style={{ width:'720px', maxWidth:'95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
                                                <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                                                    <div style={{ fontSize:18, fontWeight:700 }}>Add Land</div>
                                                    <button onClick={()=> setShowAddLand(false)} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
                                                </div>
                                                <div style={{ padding:16 }}>
                                                    {landFormError && <div style={{ color:'#f87171', marginBottom:8 }}>{landFormError}</div>}
                                                    <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                                                        <div>
                                                            <label className="form-label">Legal Description</label>
                                                            <input className="form-control" value={landForm.legal_description} onChange={e=> setLandForm(prev=> ({ ...prev, legal_description: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className="form-label">Parcel Number</label>
                                                            <input className="form-control" value={landForm.parcel_number} onChange={e=> setLandForm(prev=> ({ ...prev, parcel_number: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className="form-label">Lot Number</label>
                                                            <input className="form-control" type="number" value={landForm.lot_number} onChange={e=> setLandForm(prev=> ({ ...prev, lot_number: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className="form-label">Plat Number</label>
                                                            <input className="form-control" type="number" value={landForm.plat_number} onChange={e=> setLandForm(prev=> ({ ...prev, plat_number: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className="form-label">Tax District</label>
                                                            <input className="form-control" value={landForm.tax_district} onChange={e=> setLandForm(prev=> ({ ...prev, tax_district: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className="form-label">Area (acres)</label>
                                                            <input className="form-control" type="number" step="0.0001" value={landForm.area_in_acres} onChange={e=> setLandForm(prev=> ({ ...prev, area_in_acres: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className="form-label">Allocate Financing to Land (optional)</label>
                                                            <input className="form-control" type="number" step="0.01" value={landForm.financing_allocation} onChange={e=> setLandForm(prev=> ({ ...prev, financing_allocation: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className="form-label">Latitude (optional)</label>
                                                            <input className="form-control" type="number" step="0.000001" value={landForm.latitude} onChange={e=> setLandForm(prev=> ({ ...prev, latitude: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className="form-label">Longitude (optional)</label>
                                                            <input className="form-control" type="number" step="0.000001" value={landForm.longitude} onChange={e=> setLandForm(prev=> ({ ...prev, longitude: e.target.value }))} />
                                                        </div>
                                                        <div style={{ gridColumn:'1 / -1' }}>
                                                            <label className="form-label">Survey Description (optional)</label>
                                                            <textarea className="form-control" rows={3} value={landForm.survey_description} onChange={e=> setLandForm(prev=> ({ ...prev, survey_description: e.target.value }))} />
                                                        </div>
                                                    </div>
                                                    <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                                                        <button className="btn btn-secondary" onClick={()=> setShowAddLand(false)} disabled={landFormLoading}>Cancel</button>
                                                        <button className="btn btn-success" onClick={submitAddLand} disabled={landFormLoading || !selectedPropertyId || !landForm.legal_description || !landForm.parcel_number || !landForm.lot_number || !landForm.plat_number || !landForm.tax_district || !landForm.area_in_acres}>
                                                            {landFormLoading ? 'Saving…' : 'Save Land'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {showHomeModal && (
                        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
                          <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(680px, 90vw)' }}>
                            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                              <h5 style={{ margin:0 }}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===homeTxnId); return (t && t.linked_asset_id) ? 'Edit Link — Home Expense' : 'Link Home Expense'; } catch(e){ return 'Link Home Expense'; } })()}</h5>
                              <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                                {(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===homeTxnId); return (t && t.linked_asset_id); } catch(e){ return false; } })() && (
                                  <button
                                    className="btn btn-sm btn-warning"
                                    title="Unlink from asset"
                                    onClick={async ()=>{
                                      try {
                                        await api.post(`/balance_sheet/financing/revolvers/transactions/${homeTxnId}/unlink-asset-expense/`);
                                        setShowHomeModal(false);
                                        setHomeTxnId(null);
                                        await fetchTransactions();
                                      } catch(err){
                                        console.error('Failed to unlink home expense', err);
                                        setHomeError('Failed to unlink home expense.');
                                      }
                                    }}
                                  >Unlink</button>
                                )}
                                <button className="btn btn-sm btn-secondary" onClick={()=> setShowHomeModal(false)}>✕</button>
                              </div>
                            </div>
                            <div style={{ padding:16 }}>
                    {homeError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{homeError}</div>}
                    <div className="mb-3">
                        <label className="form-label">Residential Property</label>
                        <select className="form-select" value={homeSelectedPropertyId} onChange={(e)=> onChangePropertyForHome(e.target.value)} disabled={homeLoading}>
                            <option value="">Select property…</option>
                            {homeProperties.map(p => (<option key={p.id} value={p.id}>{p.address}</option>))}
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Structure</label>
                        <select className="form-select" value={homeSelectedAssetId} onChange={(e)=> setHomeSelectedAssetId(e.target.value)} disabled={homeLoading || !homeSelectedPropertyId}>
                            <option value="">{homeSelectedPropertyId ? 'Select structure…' : 'Select property first'}</option>
                            {homeAssetOptions.map(opt => (<option key={opt.id} value={opt.id}>{opt.label}</option>))}
                        </select>
                        <small className="form-text text-muted">If no structures are available, please add a structure first.</small>
                    </div>
                    <div className="mb-3">
                        <label className="form-check">
                            <input className="form-check-input" type="checkbox" checked={homeIsRecurring} onChange={e=> setHomeIsRecurring(e.target.checked)} />
                            <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                        </label>
                        {homeIsRecurring && (
                          <div style={{ marginTop:8 }}>
                            <div className="btn-group btn-group-sm" role="group" aria-label="Recurring mode">
                              <button type="button" className={`btn ${homeRecurringMode==='existing' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=>{ setHomeRecurringMode('existing'); if (homeRecurringChoice === '__new__') setHomeRecurringChoice(''); }}>
                                Existing
                              </button>
                              <button type="button" className={`btn ${homeRecurringMode==='new' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=>{ setHomeRecurringMode('new'); setHomeRecurringChoice('__new__'); }}>
                                New
                              </button>
                            </div>

                            {homeRecurringMode === 'existing' && (
                              <div style={{ marginTop:8 }}>
                                <label className="form-label">Existing recurring series</label>
                                <select
                                  className="form-select"
                                  value={homeRecurringChoice}
                                  onChange={(e)=> setHomeRecurringChoice(e.target.value)}
                                  disabled={!homeSelectedAssetId || homeRecurringLoading}
                                  onFocus={async()=> { if (homeSelectedPropertyId && !homeSeriesOptions.length) {
                                    try {
                                      setHomeRecurringLoading(true);
                                      const res = await api.get(`/balance_sheet/assets/physical_assets/${homeSelectedPropertyId}/recurring-series/`, { params: { include_children: 1, include_inactive: 1 } });
                                      const items = Array.isArray(res.data) ? res.data.map(s => ({ id: String(s.id), name: s.name })) : [];
                                      setHomeSeriesOptions(items);
                                    } catch (e) { setHomeSeriesOptions([]); }
                                    finally { setHomeRecurringLoading(false); }
                                  } }}
                                >
                                  <option value="">{homeRecurringLoading ? 'Loading…' : 'Select existing series…'}</option>
                                  {homeSeriesOptions.map(opt => (
                                    <option key={opt.id} value={String(opt.id)}>
                                      {opt.name}
                                    </option>
                                  ))}
                                </select>
                                {!homeSeriesOptions.length && <small className="form-text text-muted">No existing recurring series found for this asset.</small>}
                              </div>
                            )}

                            {homeRecurringMode === 'new' && (
                              <div style={{ marginTop:8 }}>
                                <div className="mb-2">
                                  <label className="form-label">New series name</label>
                                  <input className="form-control" type="text" value={homeRecurringName} onChange={(e)=> setHomeRecurringName(e.target.value)} placeholder="e.g., Property Tax" />
                                </div>
                                <div className="mb-2">
                                  <label className="form-label">Frequency</label>
                                  <select className="form-select" value={homeFrequency} onChange={(e)=> setHomeFrequency(e.target.value)}>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="bi_monthly">Bi-monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="semi_annually">Semi-annually</option>
                                    <option value="annually">Annually</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label">Series type</label>
                                  <select className="form-select" value={homeRecurringType} onChange={(e)=> setHomeRecurringType(e.target.value)}>
                                    <option value="fixed">Fixed</option>
                                    <option value="variable">Variable</option>
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-secondary" onClick={()=> setShowHomeModal(false)} disabled={homeLoading}>Cancel</button>
                        <button className="btn btn-primary" onClick={submitHomeLink} disabled={homeLoading || !homeSelectedPropertyId || !homeSelectedAssetId || !homeUiCategory}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===homeTxnId); return (t && t.linked_asset_id) ? 'Save' : 'Link Expense'; } catch(e){ return 'Link Expense'; } })()}</button>
                    </div>
                </div>
                          </div>
                        </div>
                    )}

                    {showAutoModal && (
                        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                          <div style={{ backgroundColor: '#0b1220', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, width: 'min(720px, 95vw)', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                            <div style={{ padding: '12px 16px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <h5 style={{ margin: 0 }}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===autoTxnId); return (t && t.linked_asset_id) ? 'Edit Link — Vehicle Expense' : 'Link Vehicle Expense'; } catch(e){ return 'Link Vehicle Expense'; } })()}</h5>
                              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                                {(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===autoTxnId); return (t && t.linked_asset_id); } catch(e){ return false; } })() && (
                                  <button
                                    className="btn btn-sm btn-warning"
                                    title="Unlink from asset"
                                    onClick={async ()=>{
                                      try {
                                        await api.post(`/balance_sheet/financing/revolvers/transactions/${autoTxnId}/unlink-asset-expense/`);
                                        await fetchTransactions();
                                        setShowAutoModal(false);
                                        setAutoTxnId(null);
                                      } catch(err){
                                        console.error('Failed to unlink vehicle expense', err);
                                        setAutoForm(prev=> ({ ...prev, error:'Failed to unlink vehicle expense.' }));
                                      }
                                    }}
                                  >Unlink</button>
                                )}
                                <button className="btn btn-sm btn-secondary" onClick={()=>{ setShowAutoModal(false); setAutoTxnId(null); }}>✕</button>
                              </div>
                            </div>
                            <div style={{ padding: 16 }}>
                              {autoForm.error && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{autoForm.error}</div>}
                              <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                                <div>
                                  <label className="form-label">Vehicle</label>
                                  <select className="form-select" value={autoForm.vehicleId} onChange={e=> onChangeVehicle(e.target.value)}>
                                    <option value="">Select vehicle…</option>
                                    {vehicles.map(v => (<option key={v.id} value={v.id}>{v.description || `Vehicle #${v.id}`}</option>))}
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label">Asset Expense Category</label>
                                  <select className="form-select" value={autoForm.assetExpenseCategory} onChange={e=> setAutoForm(prev=> ({ ...prev, assetExpenseCategory:e.target.value }))}>
                                    {(assetExpenseChoices.length ? assetExpenseChoices : [
                                      { value:'maintenance', label:'Asset Maintenance' },
                                      { value:'cleaning', label:'Cleaning' },
                                      { value:'repair', label:'Asset Repair' },
                                      { value:'enhancement', label:'Asset Enhancement' },
                                      { value:'insurance', label:'Insurance Payment' },
                                      { value:'taxes', label:'Tax Payment' }
                                    ]).map(c => (<option key={c.value} value={c.value}>{c.label}</option>))}
                                  </select>
                                </div>
                              </div>
                              <div style={{ marginTop:12 }}>
                                <label className="form-check">
                                  <input className="form-check-input" type="checkbox" checked={autoForm.isRecurring} onChange={e=> setAutoForm(prev=> ({ ...prev, isRecurring:e.target.checked }))} />
                                  <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                                </label>
                              </div>
                              {autoForm.isRecurring && (
                                <div style={{ marginTop:12, border:'1px solid #374151', borderRadius:6, padding:12 }}>
                                  <div style={{ marginBottom:8 }}>
                                    <label className="form-check" style={{ marginRight:12 }}>
                                      <input className="form-check-input" type="radio" name="schedmode" checked={autoForm.scheduleMode==='existing'} onChange={()=> setAutoForm(prev=> ({ ...prev, scheduleMode:'existing' }))} />
                                      <span className="form-check-label" style={{ marginLeft:8 }}>Link to existing schedule</span>
                                    </label>
                                    <label className="form-check" style={{ marginLeft:16 }}>
                                      <input className="form-check-input" type="radio" name="schedmode" checked={autoForm.scheduleMode==='new'} onChange={()=> setAutoForm(prev=> ({ ...prev, scheduleMode:'new' }))} />
                                      <span className="form-check-label" style={{ marginLeft:8 }}>Create new schedule</span>
                                    </label>
                                  </div>
                                  {autoForm.scheduleMode==='existing' ? (
                                    <div>
                                      <label className="form-label">Maintenance Schedule</label>
                                      <select className="form-select" value={autoForm.scheduleId} onChange={e=> setAutoForm(prev=> ({ ...prev, scheduleId:e.target.value }))}>
                                        <option value="">Select…</option>
                                        {schedules.map(s => (<option key={s.id} value={s.id}>{s.description} — expected {s.expected_maintenance_date}</option>))}
                                      </select>
                                    </div>
                                  ) : (
                                    <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                                      <div>
                                        <label className="form-label">Description</label>
                                        <input className="form-control" type="text" value={autoForm.newSchedule.description} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule:{ ...prev.newSchedule, description:e.target.value } }))} />
                                      </div>
                                      <div>
                                        <label className="form-label">Frequency</label>
                                        <select className="form-select" value={autoForm.newSchedule.frequency} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule:{ ...prev.newSchedule, frequency:e.target.value } }))}>
                                          <option value="weekly">Weekly</option>
                                          <option value="monthly">Monthly</option>
                                          <option value="bi_monthly">Bi-monthly</option>
                                          <option value="quarterly">Quarterly</option>
                                          <option value="semi_annually">Semi-annually</option>
                                          <option value="annually">Annually</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="form-label">Expected Date</label>
                                        <input className="form-control" type="date" value={autoForm.newSchedule.expected_date} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule:{ ...prev.newSchedule, expected_date:e.target.value } }))} />
                                      </div>
                                      <div>
                                        <label className="form-label">Expected Amount</label>
                                        <input className="form-control" type="number" step="0.01" value={autoForm.newSchedule.expected_amount} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule:{ ...prev.newSchedule, expected_amount:e.target.value } }))} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                                <button className="btn btn-secondary" onClick={()=> { setShowAutoModal(false); setAutoTxnId(null); }}>Cancel</button>
                                <button className="btn btn-success" onClick={submitAutoLink} disabled={autoForm.loading || !autoForm.vehicleId}>
                                  {autoForm.loading ? 'Linking…' : 'Link Expense'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                    )}

                    {!switchingCard && (
                        <>
                            {/* Charts Section */}
                            <div className="mb-5">
                                {statementSummaryData && (
                                    <div className="chart-card">
                                        <GroupedBarChart
                                            data={statementSummaryData}
                                            selectedStatementId={selectedStatement}
                                            isAllCardsView={id === 'all'}  // Pass this prop to the chart
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Transactions Section */}
                            <div className="chart-card">
                                <div className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h3 className="card-title mb-1">Transactions</h3>
                                            <p className="card-text text-muted mb-0">
                                                {isAllCardsView || id === 'all'
                                                    ? 'View transactions across all your credit cards'
                                                    : 'View and manage your credit card transactions'
                                                }
                                            </p>
                                        </div>
                                        {!(isAllCardsView || id === 'all') && (
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <ExcelUpload
                                                    apiEndpoint={excelApiEndpoint}
                                                    lender={creditCard?.lender}
                                                    object_id={creditCard?.id}
                                                    existingStatements={statements}
                                                    onSuccess={async () => {
                                                        await fetchTransactions();
                                                        await fetchStatementSummaryData(true);
                                                    }}
                                                />
                                                <button
                                                    className="btn btn-success"
                                                    onClick={openAddModal}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}
                                                >
                                                    <span style={{ fontSize: '18px' }}>+</span>
                                                    Add Transaction
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    {/* Statement Selector */}
                                    <div className="row mb-4">
                                        <div className="col-md-4">
                                            <label htmlFor="statement-selector" className="form-label fw-semibold">Filter by Statement:</label>
                                            <select
                                                id="statement-selector"
                                                className="form-select"
                                                value={selectedStatement}
                                                onChange={(e) => handleStatementChange(e.target.value)}
                                            >
                                                <option value="current">Current</option>
                                                {Array.isArray(statements) && [...statements]
                                                    .sort((a, b) => new Date(b.billing_cycle_end_date) - new Date(a.billing_cycle_end_date)) // Sort most recent first
                                                    .map(statement => (
                                                        <option key={statement.id} value={statement.id}>
                                                            {statement.statement_month_int}/{statement.statement_year_int}
                                                            ({formatDate(statement.billing_cycle_start_date)} - {formatDate(statement.billing_cycle_end_date)})
                                                        </option>
                                                    ))}
                                            </select>
                                            <small className="form-text text-muted">
                                                Select a specific statement to filter transactions and charts.
                                                {statements.length === 1 && " Additional statements will load when selected."}
                                            </small>
                                        </div>
                                    </div>


                                    {/* Pie Chart */}
                                    <div className="mb-4">
                                        <TransactionsPieChart transactions={chartTransactions} />
                                    </div>

                                    {/* Transactions Table */}

                                    {showUtilityModal && utilityRow && (
                                      <AssetExpenseLinkModal
                                        visible={showUtilityModal}
                                        onClose={()=> { setShowUtilityModal(false); setUtilityRow(null); }}
                                        sourceType="revolver"
                                        transaction={utilityRow}
                                        domain={getDomainForCategory(utilityRow.expense_category)}
                                        // No property preselected on CreditCardDetail; let modal show Property -> Structure pickers
                                        mediaConfig={{
                                          categories: [
                                            { value: 'media_internet', label: 'Internet' },
                                            { value: 'media_cable_tv', label: 'Cable/TV' },
                                            { value: 'media_phone', label: 'Phone' },
                                            { value: 'media_streaming', label: 'Streaming Services' },
                                            { value: 'media_books', label: 'Books' },
                                            { value: 'media_other', label: 'Other Media' },
                                          ],
                                        }}
                                        utilitiesConfig={{
                                          propertyId: null,
                                          categories: [
                                            { value: 'utilities_electric_gas', label: 'Electric & Gas' },
                                            { value: 'utilities_water', label: 'Water' },
                                            { value: 'utilities_trash', label: 'Trash/Waste' },
                                            { value: 'utilities_sewer', label: 'Sewer' },
                                            { value: 'utilities_internet', label: 'Internet' },
                                            { value: 'utilities_cable_tv', label: 'Cable/TV' },
                                            { value: 'utilities_phone', label: 'Phone' },
                                          ]
                                        }}
                                        // If a previous link exists, prefill the asset selection and linkage hints
                                        initialValues={{
                                          asset_id: utilityRow.linked_asset_id || '',
                                          // Provide current utilities sub-category to enable exact prefill
                                          expense_category: utilityRow.expense_category || '',
                                          // Derive property id from the linked asset so the modal can preselect the Property
                                          property_id: (()=>{ try {
                                            const aid = utilityRow.linked_asset_id;
                                            if (!aid) return '';
                                            // CreditCardDetail does not have an allAssets cache; rely on txn payload hints if present
                                            const rpId = utilityRow.linked_property_id || utilityRow.parent_property_id || '';
                                            return rpId ? String(rpId) : '';
                                          } catch(e){ return ''; } })(),
                                          recurring_series_id: utilityRow.linked_recurring_series_id || '',
                                          utility_bill_id: utilityRow.linked_utility_bill_id || '',
                                        }}
                                        // After a successful link/unlink, refresh transactions and close
                                        onSaved={async (payload)=> { 
                                          await fetchTransactions(); 
                                          setShowUtilityModal(false); 
                                          setUtilityRow(null); 
                                        }}
                                        onUnlinked={async ()=> { 
                                          await fetchTransactions(); 
                                          setShowUtilityModal(false); 
                                          setUtilityRow(null); 
                                        }}
                                      />
                                    )}
                                      <ReceiptModal
                                        isOpen={showReceiptModal}
                                        onClose={closeReceipt}
                                        url={receiptPreviewUrl}
                                        title={receiptModalTitle}
                                        onDelete={deleteReceipt}
                                      />
                                                                        {showIncomeSplitModal && incomeSplitRow && (
                                                                          <IncomeSplitModal
                                                                            row={incomeSplitRow}
                                                                            defaultKind={incomeSplitDefaultKind}
                                                                            onClose={()=> { setShowIncomeSplitModal(false); setIncomeSplitRow(null); }}
                                                                            onCompleted={async ()=> { await fetchTransactions(); }}
                                                                          />
                                                                        )}
                                                                        {showIncomeLinkModal && incomeLinkRow && (
                        <IncomeLinkModal
                            row={incomeLinkRow}
                            onClose={() => { setShowIncomeLinkModal(false); setIncomeLinkRow(null); }}
                            onLinked={async () => { await fetchTransactions(); }}
                        />
                    )}
                    {showDebtDisbursementModal && debtDisbursementRow && (
                        <DebtDisbursementLinkModal
                            row={debtDisbursementRow}
                            onClose={() => { setShowDebtDisbursementModal(false); setDebtDisbursementRow(null); }}
                            onLinked={async () => { await fetchTransactions(); }}
                        />
                    )}
                                                                        {showWasteModal && (
                                                                          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
                                                                            <div style={{ width: '720px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
                                                                              <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                                                                                <div style={{ fontSize:18, fontWeight:700 }}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===wasteTxnId); return (t && t.linked_asset_id) ? 'Edit Link — Waste Management Expense' : 'Link Waste Management Expense'; } catch(e){ return 'Link Waste Management Expense'; } })()}</div>
                                                                                <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                                                                                  {(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===wasteTxnId); return (t && t.linked_asset_id); } catch(e){ return false; } })() && (
                                                                                    <button
                                                                                      className="btn btn-sm btn-warning"
                                                                                      title="Unlink from asset"
                                                                                      onClick={async ()=>{
                                                                                        try {
                                                                                          await api.post(`/balance_sheet/financing/revolvers/transactions/${wasteTxnId}/unlink-asset-expense/`);
                                                                                          setShowWasteModal(false);
                                                                                          setWasteTxnId(null);
                                                                                          await fetchTransactions();
                                                                                        } catch(err){
                                                                                          console.error('Failed to unlink waste expense', err);
                                                                                          setWasteError('Failed to unlink waste expense.');
                                                                                        }
                                                                                      }}
                                                                                    >Unlink</button>
                                                                                  )}
                                                                                  <button onClick={()=>{ setShowWasteModal(false); setWasteTxnId(null); }} className="btn btn-sm btn-secondary">✕</button>
                                                                                </div>
                                                                              </div>
                                                                              <div style={{ padding:16 }}>
                                                                                {wasteError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{wasteError}</div>}
                                                                                <div className="mb-3">
                                                                                  <label className="form-label">Residential Property</label>
                                                                                  <select className="form-select" value={wasteSelectedPropertyId} onChange={(e)=> onChangePropertyForWaste(e.target.value)} disabled={wasteLoading}>
                                                                                    <option value="">Select property…</option>
                                                                                    {wasteProperties.map(p => (<option key={p.id} value={p.id}>{p.address}</option>))}
                                                                                  </select>
                                                                                </div>
                                                                                <div className="mb-3">
                                                                                  <label className="form-label">Structure</label>
                                                                                  <select className="form-select" value={wasteSelectedAssetId} onChange={(e)=> setWasteSelectedAssetId(e.target.value)} disabled={wasteLoading || !wasteSelectedPropertyId}>
                                                                                    <option value="">{wasteSelectedPropertyId ? 'Select structure…' : 'Select property first'}</option>
                                                                                    {wasteAssetOptions.map(opt => (<option key={opt.id} value={opt.id}>{opt.label}</option>))}
                                                                                  </select>
                                                                                  <small className="form-text text-muted">Expenses will roll up to the structure and aggregate to the property.</small>
                                                                                </div>
                                                                                <div className="mb-3">
                                                                                  <label className="form-check">
                                                                                    <input className="form-check-input" type="checkbox" checked={wasteIsRecurring} onChange={(e)=> setWasteIsRecurring(e.target.checked)} />
                                                                                    <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                                                                                  </label>
                                                                                  {wasteIsRecurring && (
                                                                                    <div style={{ marginTop:8 }}>
                                                                                      <div className="btn-group btn-group-sm" role="group" aria-label="Recurring mode">
                                                                                        <button type="button" className={`btn ${wasteRecurringMode==='existing' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=> setWasteRecurringMode('existing')}>Existing</button>
                                                                                        <button type="button" className={`btn ${wasteRecurringMode==='new' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=> setWasteRecurringMode('new')}>New</button>
                                                                                      </div>
                                                                                      {wasteRecurringMode === 'existing' && (
                                                                                        <div style={{ marginTop:8 }}>
                                                                                          <label className="form-label">Existing recurring series</label>
                                                                                          <select className="form-select" value={wasteExistingSeriesName} onChange={(e)=> setWasteExistingSeriesName(e.target.value)} disabled={!wasteSelectedAssetId}>
                                                                                            <option value="">-- Select series --</option>
                                                                                            {wasteSeriesOptions.map(s => (
                                                                                              <option key={s.id} value={s.id}>{s.name}</option>
                                                                                            ))}
                                                                                          </select>
                                                                                          {!wasteSeriesOptions.length && <small className="form-text text-muted">No existing recurring series found for this structure.</small>}
                                                                                        </div>
                                                                                      )}
                                                                                      {wasteRecurringMode === 'new' && (
                                                                                        <div style={{ marginTop:8 }}>
                                                                                          <div className="mb-2">
                                                                                            <label className="form-label">New series name</label>
                                                                                            <input className="form-control" type="text" value={wasteRecurringName} onChange={(e)=> setWasteRecurringName(e.target.value)} placeholder="e.g., Sanitation - Monthly" />
                                                                                          </div>
                                                                                          <div className="mb-2">
                                                                                            <label className="form-label">Frequency</label>
                                                                                            <select className="form-select" value={wasteFrequency} onChange={(e)=> setWasteFrequency(e.target.value)}>
                                                                                              <option value="weekly">Weekly</option>
                                                                                              <option value="monthly">Monthly</option>
                                                                                              <option value="bi_monthly">Bi-monthly</option>
                                                                                              <option value="quarterly">Quarterly</option>
                                                                                              <option value="semi_annually">Semi-annually</option>
                                                                                              <option value="annually">Annually</option>
                                                                                            </select>
                                                                                          </div>
                                                                                          <div>
                                                                                            <label className="form-label">Series type</label>
                                                                                            <select className="form-select" value={wasteRecurringType} onChange={(e)=> setWasteRecurringType(e.target.value)}>
                                                                                              <option value="fixed">Fixed</option>
                                                                                              <option value="variable">Variable</option>
                                                                                            </select>
                                                                                          </div>
                                                                                        </div>
                                                                                      )}
                                                                                    </div>
                                                                                  )}
                                                                                </div>
                                                                                <div className="d-flex justify-content-end gap-2">
                                                                                  <button className="btn btn-secondary" onClick={()=> { setShowWasteModal(false); setWasteTxnId(null); }} disabled={wasteLoading}>Cancel</button>
                                                                                  <button className="btn btn-primary" onClick={submitWasteLink} disabled={wasteLoading || !wasteSelectedPropertyId || !wasteSelectedAssetId}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===wasteTxnId); return (t && t.linked_asset_id) ? 'Save' : 'Link Expense'; } catch(e){ return 'Link Expense'; } })()}</button>
                                                                                </div>
                                                                              </div>
                                                                            </div>
                                                                          </div>
                                                                        )}
                                                                        <div className="table-responsive" style={{ overflowX: 'visible' }}>
                                                                           <div style={{ marginBottom: '10px' }}>
                                                                               <button 
                                                                                   onClick={handleExport} 
                                                                                   className="btn btn-outline-success btn-sm"
                                                                                   title="Export to Excel"
                                                                               >
                                                                                   <ExcelIcon /> Export
                                                                               </button>
                                                                           </div>
                                                                        <table>
                                            <thead>
                                                <tr>
                                                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('transaction_date')}>
                                                        Transaction Date <SortIcon field="transaction_date"/>
                                                    </th>
                                                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('post_date')}>
                                                        Post Date <SortIcon field="post_date"/>
                                                    </th>
                                                    {/* Card column - only show for "All Cards" view */}
                                                    {id === 'all' && (
                                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('card_display')}>
                                                            Card <SortIcon field="card_display"/>
                                                        </th>
                                                    )}
                                                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('description')}>
                                                        Description <SortIcon field="description"/>
                                                    </th>
                                                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('user_description')}>
                                                        User Description <SortIcon field="user_description"/>
                                                    </th>
                                                    <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('amount')}>
                                                        Amount <SortIcon field="amount"/>
                                                    </th>
                                                    <th scope="col" style={{ cursor: 'pointer', minWidth:'220px', whiteSpace:'nowrap' }} onClick={() => handleSort('expense_category')}>
                                                        Category <SortIcon field="expense_category"/>
                                                    </th>
                                                    <th scope="col">Asset Link</th>
                                                    <th scope="col">Linked To-Do</th>
                                                    <th scope="col">Recurring Series</th>
                                                    <th scope="col">Debt Link</th>
                                                    <th scope="col">Receipt</th>
                                                    <th scope="col">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {processedTransactions.length > 0 ? (
                                                    processedTransactions.map((transaction) => (
                                                        <tr key={transaction.id}
                                                            className={deleting.has(transaction.id) ? 'table-secondary' : ''}
                                                            style={{
                                                                backgroundColor: transaction._isChild ? '#1f2a44' : 'transparent',
                                                                borderLeft: transaction._isChild ? '3px solid #60a5fa' : 'none'
                                                            }}
                                                        >
                                                            <td>
                                                                <MemoizedEditableCell
                                                                    transaction={transaction}
                                                                    field="transaction_date"
                                                                    displayValue={transaction.transaction_date ? formatDate(transaction.transaction_date) : ''}
                                                                    isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'transaction_date'}
                                                                    onStartEdit={startEditing}
                                                                    onCancel={cancelEditing}
                                                                    onSave={saveEdit}
                                                                    saving={saving}
                                                                />
                                                            </td>
                                                            <td>
                                                                <MemoizedEditableCell
                                                                    transaction={transaction}
                                                                    field="post_date"
                                                                    displayValue={transaction.post_date ? formatDate(transaction.post_date) : ''}
                                                                    isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'post_date'}
                                                                    onStartEdit={startEditing}
                                                                    onCancel={cancelEditing}
                                                                    onSave={saveEdit}
                                                                    saving={saving}
                                                                />
                                                            </td>
                                                            {/* Card column - only show for "All Cards" view */}
                                                            {id === 'all' && (
                                                                <td>
                                                                    {transaction.card_display || 'N/A'}
                                                                </td>
                                                            )}
                                                            <td>
                                                                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                                                    {/* Expand/collapse toggle for parents with children */}
                                                                    {(!transaction._isChild && transaction.has_children) ? (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setExpandedTxns(prev => {
                                                                                    const n = new Set([...prev]);
                                                                                    if (n.has(transaction.id)) n.delete(transaction.id); else n.add(transaction.id);
                                                                                    return n;
                                                                                });
                                                                            }}
                                                                            title={expandedTxns.has(transaction.id) ? 'Collapse' : 'Expand'}
                                                                            style={{ padding:'2px 6px', fontSize: '10px', minWidth: '20px' }}
                                                                        >
                                                                            {expandedTxns.has(transaction.id) ? '−' : '+'}
                                                                        </button>
                                                                    ) : (
                                                                        !transaction._isChild && <span style={{ width: 20 }}></span>
                                                                    )}
                                                                    <div style={transaction._isChild ? { paddingLeft: 24 } : {}}>
                                                                        <MemoizedEditableCell
                                                                            transaction={transaction}
                                                                            field="description"
                                                                            displayValue={transaction.description || ''}
                                                                            isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'description'}
                                                                            onStartEdit={startEditing}
                                                                            onCancel={cancelEditing}
                                                                            onSave={saveEdit}
                                                                            saving={saving}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <MemoizedEditableCell
                                                                    transaction={transaction}
                                                                    field="user_description"
                                                                    displayValue={transaction.user_description || ''}
                                                                    isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'user_description'}
                                                                    onStartEdit={startEditing}
                                                                    onCancel={cancelEditing}
                                                                    onSave={saveEdit}
                                                                    saving={saving}
                                                                />
                                                            </td>
                                                            <td>
                                                                <MemoizedEditableCell
                                                                    transaction={transaction}
                                                                    field="amount"
                                                                    displayValue={formatCurrency(transaction.amount || 0)}
                                                                    isEditing={editingCell?.transactionId === transaction.id && editingCell?.field === 'amount'}
                                                                    onStartEdit={startEditing}
                                                                    onCancel={cancelEditing}
                                                                    onSave={saveEdit}
                                                                    saving={saving}
                                                                />
                                                            </td>
                                                            <td style={{ minWidth:'220px', whiteSpace:'nowrap' }}>
                                                                {renderExpenseCategory(transaction)}
                                                            </td>
                                                            <td>
                                                                <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                                                                    {(() => {
                                                                        const aid = transaction.linked_asset_id;
                                                                        if (!aid) {
                                                                            const cat = String(transaction.expense_category || '');
                                                                            const domain = getDomainForCategory(cat);
                                                                            const isYardWaste = cat.includes('yard_waste');
                                                                            const isWaste = (cat.startsWith('waste_') || cat.includes('sanitation')) && !isYardWaste;
                                                                            const isLinkable = domain !== 'generic' || isWaste || isYardWaste;
                                                                            if (!isLinkable) return null;

                                                                            let labelBtn = 'Link Asset';
                                                                            if (domain === 'utilities') labelBtn = 'Link Utilities';
                                                                            else if (domain === 'home') labelBtn = 'Link Home';
                                                                            else if (domain === 'landscaping' || isYardWaste) labelBtn = 'Link Landscaping';
                                                                            else if (domain === 'vehicle') labelBtn = 'Link Vehicle';
                                                                            else if (isWaste) labelBtn = 'Link Waste';

                                                                            return (
                                                                                <button
                                                                                    className="btn btn-link btn-sm"
                                                                                    style={{ color:'#93c5fd', padding:0 }}
                                                                                    onClick={async (e) => {
                                                                                        e.preventDefault();
                                                                                        setUtilityRow({
                                                                                            id: transaction.id,
                                                                                            sourceType: 'revolver',
                                                                                            date: transaction.transaction_date,
                                                                                            description: transaction.description,
                                                                                            amount: transaction.amount,
                                                                                            linked_asset_id: transaction.linked_asset_id || '',
                                                                                            linked_asset_expense_id: transaction.linked_asset_expense_id || '',
                                                                                            linked_utility_bill_id: transaction.linked_utility_bill_id || '',
                                                                                            linked_recurring_series_id: transaction.linked_recurring_series_id || '',
                                                                                            linked_property_id: transaction.linked_property_id || '',
                                                                                            expense_category: transaction.expense_category || '',
                                                                                        });
                                                                                        setShowUtilityModal(true);
                                                                                    }}
                                                                                >
                                                                                    {labelBtn}
                                                                                </button>
                                                                            );
                                                                        }
                                                                        const asset = allAssets.find(a => String(a.id) === String(aid));
                                                                        const url = buildAssetUrl(asset);
                                                                        const cat = String(transaction.expense_category || '');
                                                                        const domain = getDomainForCategory(cat);
                                                                        const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: domain });

                                                                        return (
                                                                            <>
                                                                                {url ? (
                                                                                    <Link to={url} title={label} style={{ color:'#93c5fd' }}>{label}</Link>
                                                                                ) : (
                                                                                    <span title={label} style={{ color:'#93c5fd' }}>{label}</span>
                                                                                )}
                                                                                <button
                                                                                    className="btn btn-link btn-sm"
                                                                                    style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                                                                    onClick={async (e) => {
                                                                                        e.preventDefault();
                                                                                        setUtilityRow({
                                                                                            id: transaction.id,
                                                                                            sourceType: 'revolver',
                                                                                            date: transaction.transaction_date,
                                                                                            description: transaction.description,
                                                                                            amount: transaction.amount,
                                                                                            linked_asset_id: transaction.linked_asset_id || '',
                                                                                            linked_asset_expense_id: transaction.linked_asset_expense_id || '',
                                                                                            linked_utility_bill_id: transaction.linked_utility_bill_id || '',
                                                                                            linked_recurring_series_id: transaction.linked_recurring_series_id || '',
                                                                                            linked_property_id: transaction.linked_property_id || '',
                                                                                            expense_category: transaction.expense_category || '',
                                                                                        });
                                                                                        setShowUtilityModal(true);
                                                                                    }}
                                                                                >
                                                                                    Edit Link
                                                                                </button>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                                                                    {(() => {
                                                                        const todoInfo = transaction.linked_todo_info;
                                                                        if (!todoInfo) return null;

                                                                        let url = null;
                                                                        if (todoInfo.asset_type === 'vehicle') {
                                                                            url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                                                                        } else if (todoInfo.property_id) {
                                                                            url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                                                                        }

                                                                        const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                                                                        
                                                                        const content = (
                                                                            <span 
                                                                                style={{ color: '#93c5fd', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                                                title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}
                                                                            >
                                                                                {todoInfo.title}
                                                                            </span>
                                                                        );

                                                                        return (
                                                                            <>
                                                                                {url ? (
                                                                                    <Link to={url}>{content}</Link>
                                                                                ) : (
                                                                                    content
                                                                                )}
                                                                                <span style={{ fontSize: '10px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 4px', marginLeft: '4px' }}>
                                                                                    {todoInfo.status_display}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                                                                    {(() => {
                                                                        const expInfo = transaction.linked_expense_info;
                                                                        const incInfo = transaction.linked_income_info;
                                                                        
                                                                        if (expInfo && expInfo.recurring_name) {
                                                                            const aid = transaction.linked_asset_id;
                                                                            const asset = allAssets.find(a => String(a.id) === String(aid));
                                                                            const assetUrl = buildAssetUrl(asset);
                                                                            const url = assetUrl ? `${assetUrl}?series_id=${transaction.linked_recurring_series_id}` : null;
                                                                            
                                                                            return (
                                                                                <>
                                                                                    {url ? (
                                                                                        <Link to={url} style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{expInfo.recurring_name}</Link>
                                                                                    ) : (
                                                                                        <span style={{ color:'#e5e7eb' }}>{expInfo.recurring_name}</span>
                                                                                    )}
                                                                                    <button
                                                                                        className="btn btn-link btn-sm"
                                                                                        style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            setUtilityRow({
                                                                                                ...transaction,
                                                                                                sourceType: 'revolver',
                                                                                                date: transaction.transaction_date,
                                                                                                linked_asset_expense_id: transaction.linked_asset_expense_id || '',
                                                                                                linked_utility_bill_id: transaction.linked_utility_bill_id || '',
                                                                                                linked_recurring_series_id: transaction.linked_recurring_series_id || '',
                                                                                            });
                                                                                            setShowUtilityModal(true);
                                                                                        }}
                                                                                    >
                                                                                        Edit Link
                                                                                    </button>
                                                                                </>
                                                                            );
                                                                        } else if (incInfo && incInfo.display_name) {
                                                                            const url = `/income?series_id=${transaction.linked_income_stream_id}`;
                                                                            return (
                                                                                <>
                                                                                    <Link to={url} style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{incInfo.display_name}</Link>
                                                                                    <button
                                                                                        className="btn btn-link btn-sm"
                                                                                        style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            setIncomeLinkRow(transaction);
                                                                                            setShowIncomeLinkModal(true);
                                                                                        }}
                                                                                    >
                                                                                        Edit Link
                                                                                    </button>
                                                                                </>
                                                                            );
                                                                        }
                                                                        return null;
                                                                    })()}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                                                                    {(() => {
                                                                        const info = transaction.linked_debt_info || transaction.linkedDebt;
                                                                        if (info && (info.debt_label || info.debt_name)) {
                                                                            let url = null;
                                                                            if (info.type === 'auto_loan') url = `/balance_sheet/financing/auto_loans/${transaction.linkedDebtId || info.id}`;
                                                                            else if (info.type === 'mortgage') url = `/balance_sheet/financing/mortgages/${transaction.linkedDebtId || info.id}`;
                                                                            else if (info.type === 'credit_card' || info.type === 'revolver') url = `/balance_sheet/financing/revolvers/credit-cards/${transaction.linkedDebtId || info.id}`;
                                                                            else if (info.type === 'heloc') url = `/balance_sheet/financing/revolvers/helocs/${transaction.linkedDebtId || info.id}`;

                                                                            return (
                                                                                <>
                                                                                    {url ? (
                                                                                        <Link
                                                                                            to={url}
                                                                                            style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }}
                                                                                            title={info.display_text || ''}
                                                                                        >
                                                                                            {info.debt_label || info.debt_name || '🔗 Debt'}
                                                                                        </Link>
                                                                                    ) : (
                                                                                        <span style={{ color:'#93c5fd', whiteSpace:'nowrap' }} title={info.display_text || ''}>
                                                                                            {info.debt_label || info.debt_name || '🔗 Debt'}
                                                                                        </span>
                                                                                    )}
                                                                                    <button
                                                                                        className="btn btn-link btn-sm"
                                                                                        style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            if (String(transaction.expense_category || '') === 'debt_disbursement') {
                                                                                                setDebtDisbursementRow(transaction);
                                                                                                setShowDebtDisbursementModal(true);
                                                                                            } else {
                                                                                                // Open payment modal logic
                                                                                                setActivePaymentTxnId(transaction.id);
                                                                                                setSelectedDebt({ type: info.type || '', id: String(transaction.linkedDebtId || info.id || '') });
                                                                                                setSelectedCandidateId(transaction.linkedDebtAmortizationId || '');
                                                                                                setShowPaymentModal(true);
                                                                                                (async () => {
                                                                                                    try {
                                                                                                        setLoadingPayment(true);
                                                                                                        const res = await api.get(`${apiBase}/transactions/${transaction.id}/debts-for-payment/`);
                                                                                                        setDebtsForPayment(res.data.debts || []);
                                                                                                    } catch (e) {
                                                                                                        setDebtsForPayment([]);
                                                                                                    } finally {
                                                                                                        setLoadingPayment(false);
                                                                                                    }
                                                                                                })();
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        Edit Link
                                                                                    </button>
                                                                                </>
                                                                            );
                                                                        } else if (String(transaction.expense_category || '') === 'payments' || String(transaction.expense_category || '') === 'debt_disbursement') {
                                                                            return (
                                                                                <button
                                                                                    className="btn btn-link btn-sm"
                                                                                    style={{ color:'#93c5fd', padding:0 }}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        if (String(transaction.expense_category || '') === 'debt_disbursement') {
                                                                                            setDebtDisbursementRow(transaction);
                                                                                            setShowDebtDisbursementModal(true);
                                                                                        } else {
                                                                                            // Open payment modal logic
                                                                                            setActivePaymentTxnId(transaction.id);
                                                                                            setSelectedDebt({ type: '', id: '' });
                                                                                            setPaymentCandidates([]);
                                                                                            setSelectedCandidateId('');
                                                                                            setShowPaymentModal(true);
                                                                                            (async () => {
                                                                                                try {
                                                                                                    setLoadingPayment(true);
                                                                                                    const res = await api.get(`${apiBase}/transactions/${transaction.id}/debts-for-payment/`);
                                                                                                    setDebtsForPayment(res.data.debts || []);
                                                                                                } catch (e) {
                                                                                                    setDebtsForPayment([]);
                                                                                                } finally {
                                                                                                    setLoadingPayment(false);
                                                                                                }
                                                                                            })();
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    Link Debt
                                                                                </button>
                                                                            );
                                                                        }
                                                                        return null;
                                                                    })()}
                                                                </span>
                                                            </td>
                                                            <td>
                                                              {(transaction.has_receipt || transaction.receipt_url || transaction.receipt) ? (
                                                                <button
                                                                  className="btn btn-sm btn-outline-primary"
                                                                  onClick={() => openReceipt(transaction)}
                                                                >View</button>
                                                              ) : (
                                                                <label className="btn btn-sm btn-outline-secondary mb-0">
                                                                  Upload
                                                                  <input
                                                                    type="file"
                                                                    accept="image/*,application/pdf"
                                                                    style={{ display:'none' }}
                                                                    onChange={(e) => {
                                                                      const file = e.target.files && e.target.files[0];
                                                                      if (file) uploadReceipt(transaction.id, file);
                                                                      e.target.value = '';
                                                                    }}
                                                                  />
                                                                </label>
                                                              )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                      className="btn btn-outline-primary btn-sm"
                                                                      style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                                                      onClick={() => {
                                                                        setSplitRow({ ...transaction, sourceType: 'credit' });
                                                                        setShowSplitModal(true);
                                                                      }}
                                                                    >
                                                                      Split
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-danger btn-sm"
                                                                        onClick={() => deleteTransaction(transaction.id)}
                                                                        disabled={deleting.has(transaction.id)}
                                                                        style={{
                                                                            fontSize: '0.75rem',
                                                                            padding: '2px 8px'
                                                                        }}
                                                                    >
                                                                        {deleting.has(transaction.id) ? (
                                                                            <>
                                                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                                                Deleting...
                                                                            </>
                                                                        ) : (
                                                                            'Delete'
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={id === 'all' ? "7" : "6"} className="text-center text-muted py-4">
                                                            <div>
                                                                <p className="mb-2">No transactions found for the selected period.</p>
                                                                {!(isAllCardsView || id === 'all') && (
                                                                    <button
                                                                        className="btn btn-primary btn-sm"
                                                                        onClick={openAddModal}
                                                                    >
                                                                        Add Your First Transaction
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                        </>
                    )}
                </div>
            </div>

            {/* Split Transaction Modal */}
            <SplitTransactionModal
                isOpen={showSplitModal}
                transaction={splitRow}
                onClose={() => { setShowSplitModal(false); setSplitRow(null); }}
                onSaveSuccess={async () => {
                    setShowSplitModal(false);
                    setSplitRow(null);
                    await fetchTransactions();
                }}
                formatCurrency={formatCurrency}
            />

            {showAddModal && !(isAllCardsView || id === 'all') && (
                <Modal isOpen={true} onClose={closeAddModal} title="Add New Transaction" width={800}>
                    <form>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label htmlFor="transaction_date" className="form-label">Transaction Date *</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="transaction_date"
                                    value={newTransaction.transaction_date}
                                    onChange={(e) => setNewTransaction({
                                        ...newTransaction,
                                        transaction_date: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label htmlFor="post_date" className="form-label">Post Date *</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="post_date"
                                    value={newTransaction.post_date}
                                    onChange={(e) => setNewTransaction({
                                        ...newTransaction,
                                        post_date: e.target.value
                                    })}
                                    required
                                />
                                <small className="form-text text-muted">
                                    Post date determines which statement period the transaction belongs to.
                                </small>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="description" className="form-label">Description *</label>
                            <input
                                type="text"
                                className="form-control"
                                id="description"
                                value={newTransaction.description}
                                onChange={(e) => setNewTransaction({
                                    ...newTransaction,
                                    description: e.target.value
                                })}
                                placeholder="Enter transaction description"
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="amount" className="form-label">Amount *</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                id="amount"
                                value={newTransaction.amount}
                                onChange={(e) => setNewTransaction({
                                    ...newTransaction,
                                    amount: e.target.value
                                })}
                                placeholder="0.00"
                                required
                            />
                            <small className="form-text text-muted">
                                Enter positive values for charges/purchases, negative for credits/payments
                            </small>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="expense_category" className="form-label">Expense Category</label>
                            <select
                                className="form-select"
                                id="expense_category"
                                value={newTransaction.expense_category}
                                onChange={(e) => setNewTransaction({
                                    ...newTransaction,
                                    expense_category: e.target.value
                                })}
                            >
                                {[
                                    ...expenseCategories.items.flatMap(it => [
                                        { value: it.value, label: it.label },
                                        ...(Array.isArray(it.children) ? it.children.map(ch => ({ value: ch.value, label: `- ${ch.label}` })) : [])
                                    ]),
                                    expenseCategories.noneItem
                                ].map(category => (
                                    <option key={category.value} value={category.value}>
                                        {category.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={closeAddModal}
                                disabled={addingTransaction}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleAddTransaction}
                                disabled={addingTransaction}
                            >
                                {addingTransaction ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Adding...
                                    </>
                                ) : (
                                    'Add Transaction'
                                )}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {showPaymentModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                    <div style={{ backgroundColor: '#0b1220', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, width: 'min(680px, 90vw)', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h5 style={{ margin: 0 }}>Link Payment</h5>
                            <button className="btn btn-sm btn-secondary" onClick={closePaymentModal}>✕</button>
                        </div>
                        <div style={{ padding: 16 }}>
                            <p className="text-muted" style={{ marginTop: 0, color: '#f1f5f9' }}>Select the debt this payment belongs to, then choose the matching payment entry.</p>

                            <div className="mb-3">
                                <label className="form-label" style={{ color: '#f1f5f9' }}>Debt</label>
                                <select
                                    className="form-select"
                                    onChange={onDebtChange}
                                    value={selectedDebt.type && selectedDebt.id ? `${selectedDebt.type}|${selectedDebt.id}` : ''}
                                    style={{ backgroundColor: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
                                >
                                    <option value="">Select a debt...</option>
                                    {debtsForPayment.map(d => (
                                        <option key={`${d.type}-${d.id}`} value={`${d.type}|${d.id}`}>{d.label}</option>
                                    ))}
                                </select>
                            </div>

                            {loadingPayment && <div className="mb-2" style={{ color: '#f1f5f9' }}>Loading matching payments...</div>}

                            {selectedDebt.type && !loadingPayment && (
                                <div className="mb-3">
                                    <label className="form-label" style={{ color: '#f1f5f9' }}>Matching Payments</label>
                                    {paymentCandidates.length === 0 ? (
                                        <div className="text-muted" style={{ color: '#f1f5f9' }}>No matches found for this debt and transaction.</div>
                                    ) : (
                                        <div style={{ border: '1px solid #374151', borderRadius: 6, maxHeight: 300, overflowY: 'auto' }}>
                                            {paymentCandidates.map(c => (
                                                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #111827' }}>
                                                    <input type="radio" name="candidate" value={c.id} checked={String(selectedCandidateId) === String(c.id)} onChange={() => setSelectedCandidateId(String(c.id))} />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 600 }}>{c.description || (c.candidate_type === 'amortization_row' ? 'Amortization Schedule Payment' : 'Transaction')}</div>
                                                        <div className="text-muted" style={{ fontSize: 12, color: '#f1f5f9' }}>
                                                            {c.post_date || c.payment_date ? `Date: ${formatDate(c.post_date || c.payment_date)}` : ''}
                                                        </div>
                                                    </div>
                                                    <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(c.amount)}</div>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="d-flex justify-content-end gap-2">
                                <button className="btn btn-secondary" onClick={closePaymentModal} disabled={loadingPayment}>Cancel</button>
                                <button className="btn btn-primary" onClick={linkSelectedPayment} disabled={loadingPayment || !selectedDebt.type || !selectedCandidateId}>
                                    {loadingPayment ? 'Linking...' : 'Link Payment'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CreditCardDetail;